import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FieldPath,
  Timestamp,
  addDoc,
  collection,
  deleteField,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  writeBatch,
  where,
} from 'firebase/firestore';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { db, storage } from '../../../../shared/storage/firebase';
import type { ContactRole, SchoolContactWithId, Squad, School, User } from '../../../../types';
import type { OpsAppliedSource, OpsCheckpointDefinition, OpsCheckpointStatus, OpsFlow, OpsOutreachChannel, OpsPipelineStageId, OpsTaskStatus, OpsTemplate, OpsTemplateCheckpointRef } from '../../types';
import { applySourceToFlow, buildFlowSections, ensureFlowStateForSnapshot } from '../../utils/merge';
import { assertValidTemplateIds } from '../../utils/validate';
import { ACTIVE_OPS_LOGISTICS_BY_ID as OPS_LOGISTICS_BY_ID } from '../../catalog/activeLogisticsCatalog';
import { OPS_LOGISTICS_BY_ID as LEGACY_OPS_LOGISTICS_BY_ID } from '../../catalog/logisticsCatalog';
import type { OpsLogisticDefinition } from '../../types';
import { isLogisticComplete as sharedIsLogisticComplete } from '../../utils/completion';
import { OPS_PIPELINE_STAGES, pipelineStageLabel, resolvePipelineStage } from '../../utils/stages';
import { useAuth } from '../../../auth/contexts/AuthContext';
import SearchableSelect from '../shared/SearchableSelect';
import FirstDayTeacherPacketLogistic from '../logistics/FirstDayTeacherPacketLogistic';
import LeadTeacherAssignmentLogistic from '../logistics/LeadTeacherAssignmentLogistic';
import StudentRosterLogistic from '../logistics/StudentRosterLogistic';
import { syncSquadCalendarFromDetails } from '../../../squads/utils/calendarSync';
import { resolveActiveFlowIdentity } from '../../services/opsContext';

const OPS_STAGE_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  uncategorized: { bg: '#f1f5f9', border: '#e2e8f0', text: '#334155' },
  lead: { bg: '#f1f5f9', border: '#cbd5e1', text: '#334155' },
  committed: { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8' },
  prep: { bg: '#fffbeb', border: '#fde68a', text: '#b45309' },
  launch: { bg: '#f5f3ff', border: '#ddd6fe', text: '#6d28d9' },
  running: { bg: '#f0fdf4', border: '#bbf7d0', text: '#15803d' },
  renewal: { bg: '#f0fdfa', border: '#99f6e4', text: '#0f766e' },
};

type FlowRow = {
  id: string;
  squadTitle: string;
  partnerName: string;
  locationNames: string[];
  updatedAt?: number;
};

const TODO_MAX_LEN = 280;

type TodoAnchorRect = { top: number; left: number; right: number; bottom: number; width: number; height: number };
type TodoEditorState = {
  target: 'flow' | 'checkpoint';
  checkpointId: string;
  checkpointTitle: string;
  anchorRect: TodoAnchorRect;
  isMobile: boolean;
  dueDateDraftYmd: string;
  initialDueDateYmd: string;
  draft: string;
  initialNormalized: string;
};

type EffectiveFlowTodo = {
  note: string;
  dueDateYmd: string;
  source: 'flow' | 'checkpoint' | 'none';
  checkpointId?: string;
  checkpointTitle?: string;
};

function safeString(v: any): string {
  return String(v || '').trim();
}

function formatTime24hTo12hLabel(raw: string): string {
  const s = String(raw || '').trim();
  const m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!m) return s;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (![hh, mm].every(Number.isFinite)) return s;
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return s;
  const isPm = hh >= 12;
  const hour12 = ((hh + 11) % 12) + 1;
  const min2 = String(mm).padStart(2, '0');
  return `${hour12}:${min2}${isPm ? 'pm' : 'am'}`;
}

function formatTimeDraftForTyping(raw: string): string {
  const s0 = String(raw || '');
  if (!s0) return '';
  const s = s0.toLowerCase();

  // Split off a meridian suffix if present.
  const merMatch = s.replace(/\s+/g, '').match(/(am|pm|a|p)$/);
  const mer = merMatch ? merMatch[1] : '';
  const core = mer ? s.slice(0, -mer.length) : s;

  // Keep only digits and colon in the core.
  let cleaned = core.replace(/[^\d:]/g, '');
  // Only allow one colon.
  const firstColon = cleaned.indexOf(':');
  if (firstColon !== -1) {
    cleaned = cleaned.slice(0, firstColon + 1) + cleaned.slice(firstColon + 1).replace(/:/g, '');
  }

  // If no colon and user has typed 3-4 digits, insert a colon for readability.
  if (!cleaned.includes(':')) {
    const digits = cleaned.replace(/[^\d]/g, '');
    if (digits.length === 3) cleaned = `${digits.slice(0, 1)}:${digits.slice(1)}`;
    else if (digits.length >= 4) cleaned = `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
    else cleaned = digits;
  }

  return `${cleaned}${mer}`;
}

function parseFlexibleTimeTo24h(raw: string): { ok: true; value: string } | { ok: false; error: string } {
  const s0 = String(raw || '').trim();
  if (!s0) return { ok: true, value: '' };

  const s = s0.toLowerCase().replace(/\s+/g, '');
  const merMatch = s.match(/(am|pm|a|p)$/);
  const mer = merMatch ? merMatch[1] : null;
  const core = mer ? s.slice(0, -mer.length) : s;

  // Accept "HH:MM" or "H:MM"
  const colon = core.match(/^(\d{1,2}):(\d{1,2})$/);
  let hhStr: string | null = null;
  let mmStr: string | null = null;
  if (colon) {
    hhStr = colon[1];
    mmStr = colon[2];
  } else {
    const digits = core.replace(/[^\d]/g, '');
    if (!digits) return { ok: false, error: 'Enter a time like 3:15pm, 315p, or 15:15.' };
    if (digits.length <= 2) {
      hhStr = digits;
      mmStr = '00';
    } else if (digits.length === 3) {
      hhStr = digits.slice(0, 1);
      mmStr = digits.slice(1);
    } else {
      hhStr = digits.slice(0, 2);
      mmStr = digits.slice(-2);
    }
  }

  const hh = Number(hhStr);
  const mm = Number(mmStr);
  if (![hh, mm].every(Number.isFinite)) return { ok: false, error: 'Invalid time.' };
  if (mm < 0 || mm > 59) return { ok: false, error: 'Minutes must be 00–59.' };

  if (mer) {
    const isPm = mer === 'pm' || mer === 'p';
    if (hh < 1 || hh > 12) return { ok: false, error: 'Hour must be 1–12 when using AM/PM.' };
    const h24 = (hh % 12) + (isPm ? 12 : 0);
    return { ok: true, value: `${String(h24).padStart(2, '0')}:${String(mm).padStart(2, '0')}` };
  }

  if (hh < 0 || hh > 23) return { ok: false, error: 'Hour must be 0–23.' };
  return { ok: true, value: `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}` };
}

function guessContentTypeFromFilename(name: string): string {
  const n = String(name || '').toLowerCase();
  if (n.endsWith('.png')) return 'image/png';
  if (n.endsWith('.jpg') || n.endsWith('.jpeg')) return 'image/jpeg';
  if (n.endsWith('.webp')) return 'image/webp';
  if (n.endsWith('.gif')) return 'image/gif';
  if (n.endsWith('.svg')) return 'image/svg+xml';
  if (n.endsWith('.pdf')) return 'application/pdf';
  return 'application/octet-stream';
}

function normalizeTodoText(raw: any): string {
  const s = String(raw ?? '').replace(/\r\n/g, '\n');
  const trimmed = s.replace(/\n{3,}/g, '\n\n').trim();
  return trimmed.slice(0, TODO_MAX_LEN);
}

function pct(n: number): string {
  const x = Math.max(0, Math.min(100, Math.round(n)));
  return `${x}%`;
}

function daysFromNowLabel(ms: number): string {
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '—';
  const today = new Date();
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const tMidnight = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const delta = Math.round((tMidnight.getTime() - todayMidnight.getTime()) / (24 * 60 * 60 * 1000));
  if (delta === 0) return 'today';
  if (delta === 1) return 'tomorrow';
  if (delta === -1) return 'yesterday';
  if (delta > 1) return `in ${delta} days`;
  return `${Math.abs(delta)} days ago`;
}

function timeAgoLabel(ms: number): string {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return daysFromNowLabel(n);
}

function logisticKeyFromId(logisticId: string): string {
  return String(logisticId || '').trim().replace(/\./g, '__');
}

function isLogisticMarkedNotNeeded(flow: OpsFlow | null | undefined, logisticId: string): boolean {
  const id = String(logisticId || '').trim();
  if (!id) return false;
  const key = logisticKeyFromId(id);
  const m: any = (flow as any)?.logisticNotNeededByKey || {};
  return Boolean(m && typeof m === 'object' && m[key]);
}

function followUpAtForChannel(nowMs: number, channel: OpsOutreachChannel): number {
  const dayMs = 24 * 60 * 60 * 1000;
  const days =
    channel === 'in_person' ? 7 :
    channel === 'other' ? 3 :
    2; // email/call default
  return nowMs + (days * dayMs);
}

function dueLabelFromYmd(ymd: string): { text: string; deltaDays: number } | null {
  const s = String(ymd || '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const due = new Date(`${s}T00:00:00`);
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date();
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const dueMidnight = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const deltaDays = Math.round((dueMidnight.getTime() - todayMidnight.getTime()) / (24 * 60 * 60 * 1000));
  if (deltaDays === 0) return { text: 'Due today', deltaDays };
  if (deltaDays > 0) return { text: `Due in ${deltaDays} day${deltaDays === 1 ? '' : 's'}`, deltaDays };
  const overdue = Math.abs(deltaDays);
  return { text: `Overdue by ${overdue} day${overdue === 1 ? '' : 's'}`, deltaDays };
}

function formatMmDdFromYmd(ymd: string): string {
  const s = String(ymd || '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '—';
  return `${m[2]}/${m[3]}`;
}

function initials(name: string): string {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  const a = parts[0]?.[0] || '';
  const b = parts.length > 1 ? (parts[parts.length - 1]?.[0] || '') : '';
  return `${a}${b}`.toUpperCase();
}

function computePercentComplete(flow: OpsFlow): number {
  const tasks = Object.values(flow.taskState || {});
  const active = tasks.filter(t => t.status !== 'superseded');
  if (active.length === 0) return 0;
  const done = active.filter(t => t.status === 'done').length;
  return (done / active.length) * 100;
}

function getValueAtPath(obj: any, fieldPath: string): any {
  const parts = String(fieldPath || '').split('.').filter(Boolean);
  let cur = obj;
  for (const p of parts) {
    if (!cur || typeof cur !== 'object') return undefined;
    cur = cur[p];
  }
  return cur;
}

function withValueAtPath(obj: any, fieldPath: string, value: any): any {
  const parts = String(fieldPath || '').split('.').filter(Boolean);
  if (parts.length === 0) return obj;
  const root = obj && typeof obj === 'object' ? { ...obj } : {};
  let nextCursor = root;
  let sourceCursor = obj;
  for (let index = 0; index < parts.length; index += 1) {
    const key = parts[index];
    if (index === parts.length - 1) {
      nextCursor[key] = value;
      break;
    }
    const sourceChild = sourceCursor && typeof sourceCursor === 'object'
      ? sourceCursor[key]
      : undefined;
    const nextChild = sourceChild && typeof sourceChild === 'object' && !Array.isArray(sourceChild)
      ? { ...sourceChild }
      : {};
    nextCursor[key] = nextChild;
    nextCursor = nextChild;
    sourceCursor = sourceChild;
  }
  return root;
}

function buildNestedPatchFromPath(fieldPath: string, value: any): any {
  const UNSAFE_KEYS = new Set(['__proto__', 'prototype', 'constructor']);
  const parts = String(fieldPath || '').split('.').filter(Boolean);
  if (parts.length === 0) return {};

  const root: any = Object.create(null);
  let cur: any = root;
  for (let i = 0; i < parts.length; i += 1) {
    const key = parts[i];
    if (!key) continue;
    if (UNSAFE_KEYS.has(key)) throw new Error(`Invalid field path segment: ${key}`);
    if (i === parts.length - 1) {
      cur[key] = value;
      continue;
    }
    const next =
      cur[key] && typeof cur[key] === 'object' && !Array.isArray(cur[key])
        ? cur[key]
        : Object.create(null);
    cur[key] = next;
    cur = next;
  }
  return root;
}

function shouldSyncCalendarForSquadLogistic(fieldPath: string): boolean {
  const path = safeString(fieldPath);
  return path === 'timezone' || path.startsWith('schedule.');
}

function squadHasCompleteCalendarSchedule(squad: Partial<Squad> | null | undefined): boolean {
  const schedule = (squad as any)?.schedule || {};
  if ((squad as any)?.scheduleKind === 'single_day' || schedule.mainDate) {
    return Boolean(schedule.mainDate && schedule.startTime && schedule.endTime);
  }
  return Boolean(schedule.startDate && schedule.endDate && schedule.dayOfWeek);
}

function deepMergeInto(target: any, src: any): any {
  if (!src || typeof src !== 'object' || Array.isArray(src)) return target;
  if (!target || typeof target !== 'object' || Array.isArray(target)) return target;
  for (const k of Object.keys(src)) {
    const sv = src[k];
    const tv = target[k];
    if (sv && typeof sv === 'object' && !Array.isArray(sv) && tv && typeof tv === 'object' && !Array.isArray(tv)) {
      deepMergeInto(tv, sv);
    } else {
      target[k] = sv;
    }
  }
  return target;
}

function expandDottedTopLevelKeys(data: any): any {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data;
  const out: any = { ...(data as any) };
  for (const k of Object.keys(data as any)) {
    if (!k.includes('.')) continue;
    try {
      const patch = buildNestedPatchFromPath(k, (data as any)[k]);
      delete out[k];
      deepMergeInto(out, patch);
    } catch {
      // ignore invalid field paths; keep raw data
    }
  }
  return out;
}

function isCompleteByRule(def: OpsLogisticDefinition, value: any, derivedOk?: boolean, notNeeded?: boolean): boolean {
  // Delegate to the shared SSOT completion rule so the detail page and the
  // Overview board always produce identical readiness for the same logistics.
  // Alignment: an explicit per-flow "not needed" counts as complete regardless
  // of `allowNotApplicable`.
  return sharedIsLogisticComplete(def, value, derivedOk, notNeeded);
}

function OwnerDropdown(props: {
  ownerId: string;
  adminUsers: User[];
  adminById: Map<string, User>;
  disabled?: boolean;
  onSelect: (id: string) => void;
}): React.ReactElement {
  const { ownerId, adminUsers, adminById, disabled, onSelect } = props;
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement | null>(null);

  const owner = ownerId ? adminById.get(ownerId) : null;
  const ownerName = owner ? String(owner.displayName || owner.email || ownerId) : 'Unassigned';
  const ownerPhoto = owner ? String((owner as any).headshotUrl || '') : '';

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const root = rootRef.current;
      if (!root) return;
      if (root.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {ownerPhoto ? (
        <img
          src={ownerPhoto}
          alt={ownerName}
          style={{ width: 24, height: 24, borderRadius: 999, objectFit: 'cover', border: '1px solid #e5e7eb' }}
        />
      ) : (
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: 999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#f3f4f6',
            border: '1px solid #e5e7eb',
            fontWeight: 700,
            color: '#6b7280',
            fontSize: 10,
          }}
        >
          {initials(ownerName)}
        </div>
      )}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 10px',
          fontSize: 13,
          fontWeight: 500,
          color: ownerId ? '#374151' : '#9ca3af',
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <span>{ownerName}</span>
        <span style={{ fontSize: 10, color: '#9ca3af' }}>▼</span>
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 20,
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            minWidth: 180,
            maxHeight: 240,
            overflow: 'auto',
          }}
        >
          <button
            type="button"
            onClick={() => { onSelect(''); setOpen(false); }}
            style={{
              width: '100%',
              textAlign: 'left',
              padding: '10px 12px',
              border: 'none',
              background: !ownerId ? '#f3f4f6' : 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: 999,
                background: '#f3f4f6',
                border: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                color: '#9ca3af',
              }}
            >
              —
            </div>
            <span style={{ color: '#9ca3af' }}>Unassigned</span>
          </button>
          {adminUsers.map((u) => {
            const id = String((u as any).id);
            const name = String(u.displayName || u.email || id);
            const photo = String((u as any).headshotUrl || '');
            const isSelected = id === ownerId;
            return (
              <button
                key={id}
                type="button"
                onClick={() => { onSelect(id); setOpen(false); }}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 12px',
                  border: 'none',
                  background: isSelected ? '#f3f4f6' : 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                {photo ? (
                  <img
                    src={photo}
                    alt={name}
                    style={{ width: 24, height: 24, borderRadius: 999, objectFit: 'cover', border: '1px solid #e5e7eb' }}
                  />
                ) : (
                  <div
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 999,
                      background: '#f3f4f6',
                      border: '1px solid #e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 10,
                      fontWeight: 700,
                      color: '#6b7280',
                    }}
                  >
                    {initials(name)}
                  </div>
                )}
                <span style={{ color: '#111827', fontWeight: isSelected ? 600 : 400 }}>{name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function FlowsTab(props: {
  flowId?: string;
  hideList?: boolean;
  showBackToList?: boolean;
}): React.ReactElement {
  const { userData } = useAuth();
  const navigate = useNavigate();
  const [flows, setFlows] = useState<OpsFlow[]>([]);
  const [squads, setSquads] = useState<Squad[]>([]);
  const [legacySchool, setLegacySchool] = useState<School | null>(null);
  const [partners, setPartners] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [partnerSeason, setPartnerSeason] = useState<any>({});
  const [templates, setTemplates] = useState<OpsTemplate[]>([]);
  const [checkpointDefs, setCheckpointDefs] = useState<OpsCheckpointDefinition[]>([]);
  const [adminUsers, setAdminUsers] = useState<User[]>([]);
  const [selectedFlowId, setSelectedFlowId] = useState<string | null>(null);
  const [queryText, setQueryText] = useState('');
  const [hideSuperseded, setHideSuperseded] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [collapsedCheckpointIds, setCollapsedCheckpointIds] = useState<Record<string, boolean>>({});
  const collapsedInitializedFlowIdsRef = useRef<Set<string>>(new Set());
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [applyQuery, setApplyQuery] = useState<string>('');
  const [applyError, setApplyError] = useState<string | null>(null);
  const [updatingTemplateIdx, setUpdatingTemplateIdx] = useState<number | null>(null);
  const [updateTemplateError, setUpdateTemplateError] = useState<string | null>(null);
  const [updateTemplateErrorIdx, setUpdateTemplateErrorIdx] = useState<number | null>(null);
  const [addCheckpointOpen, setAddCheckpointOpen] = useState(false);
  const [addCheckpointPickId, setAddCheckpointPickId] = useState<string>('');
  const [draggingCheckpointId, setDraggingCheckpointId] = useState<string | null>(null);
  const [draggingSectionKey, setDraggingSectionKey] = useState<string | null>(null);
  const [dragOverCheckpointId, setDragOverCheckpointId] = useState<string | null>(null);

  const [derivedOkByRuleId, setDerivedOkByRuleId] = useState<Record<string, boolean>>({});
  const [derivedValueByRuleId, setDerivedValueByRuleId] = useState<Record<string, any>>({});
  const [billingProfile, setBillingProfile] = useState<any>({});
  const [billingProfileDraftOpen, setBillingProfileDraftOpen] = useState(false);
  const [billingProfileDraft, setBillingProfileDraft] = useState<any>({});
  const [derivedEditError, setDerivedEditError] = useState<string | null>(null);
  const [partnerContacts, setPartnerContacts] = useState<SchoolContactWithId[]>([]);
  const [billingAuthorizedContactIds, setBillingAuthorizedContactIds] = useState<string[]>([]);
  const [billingAuthorizedPickId, setBillingAuthorizedPickId] = useState<string>('');
  const [personPickByRuleId, setPersonPickByRuleId] = useState<Record<string, string>>({});
  const [personDraftByRuleId, setPersonDraftByRuleId] = useState<Record<string, { firstName: string; lastName: string; email: string; phone: string }>>({});
  const [createContactModalRuleId, setCreateContactModalRuleId] = useState<string | null>(null);
  const [mainPocCreateOpen, setMainPocCreateOpen] = useState(false);
  const [mainPocCreateError, setMainPocCreateError] = useState<string | null>(null);
  const [mainPocDraft, setMainPocDraft] = useState<{ firstName: string; lastName: string; email: string; phone: string }>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [contactDateCreateCtx, setContactDateCreateCtx] = useState<null | { logisticId: string; key: string }>(null);
  const [contactDateCreateError, setContactDateCreateError] = useState<string | null>(null);
  const [contactDateDraft, setContactDateDraft] = useState<{ firstName: string; lastName: string; email: string; phone: string }>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [logisticDraftById, setLogisticDraftById] = useState<Record<string, any>>({});
  const [logisticDirtyById, setLogisticDirtyById] = useState<Record<string, boolean>>({});
  const [logisticSaveErrorById, setLogisticSaveErrorById] = useState<Record<string, string>>({});
  const [outreachPanelOpen, setOutreachPanelOpen] = useState(false);
  const [outreachChannel, setOutreachChannel] = useState<OpsOutreachChannel>('email');
  const [outreachIncludeOtherMissing, setOutreachIncludeOtherMissing] = useState(false);
  const [outreachSelectedLogisticIds, setOutreachSelectedLogisticIds] = useState<string[]>([]);
  const [copyToast, setCopyToast] = useState<string | null>(null);
  const [todoEditor, setTodoEditor] = useState<TodoEditorState | null>(null);
  const [todoSaving, setTodoSaving] = useState(false);
  const [todoError, setTodoError] = useState<string | null>(null);
  const todoTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const todoReturnFocusRef = useRef<HTMLElement | null>(null);
  const [deletingFlow, setDeletingFlow] = useState(false);
  const [savingStage, setSavingStage] = useState(false);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!actionsMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      const root = actionsMenuRef.current;
      if (root && !root.contains(e.target as Node)) setActionsMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [actionsMenuOpen]);
  const [deleteFlowError, setDeleteFlowError] = useState<string | null>(null);
  const [editingSquadTitle, setEditingSquadTitle] = useState(false);
  const [squadTitleDraft, setSquadTitleDraft] = useState('');
  const [savingSquadTitle, setSavingSquadTitle] = useState(false);
  const [squadTitleError, setSquadTitleError] = useState<string | null>(null);

  // Basic access guard (admin-only UX). AdminProtectedRoute already wraps, but keep UI clean.
  const isAdmin = Boolean((userData as any)?.isAdmin);

  // If rendered as a detail page, allow overriding selected flow via props.
  useEffect(() => {
    const id = safeString(props.flowId);
    if (!id) return;
    setSelectedFlowId(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.flowId]);

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'partnerSeasonFlows')), (snap) => {
      const list = snap.docs.map(d => ({ ...(d.data() as any), id: d.id } as OpsFlow));
      list.sort((a, b) => Number(b.updatedAt || 0) - Number(a.updatedAt || 0));
      setFlows(list);
      // Only auto-select the first flow when we're in the combined list+detail view.
      if (!props.hideList && !selectedFlowId && list[0]?.id) setSelectedFlowId(list[0].id);
      const requestedId = safeString(props.flowId);
      if (requestedId && !list.some((flow) => flow.id === requestedId)) {
        void getDoc(doc(db, 'opsFlows', requestedId)).then((legacy) => {
          if (!legacy.exists()) return;
          setFlows((current) => [
            ...current.filter((flow) => flow.id !== requestedId),
            { id: legacy.id, ...(legacy.data() as any), readOnlyHistorical: true } as OpsFlow,
          ]);
        });
      }
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Admin users list for internal owner assignment.
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'users'), where('isAdmin', '==', true)),
      (snap) => {
        const list = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) } as User));
        list.sort((a, b) => String(a.displayName || a.email || '').localeCompare(String(b.displayName || b.email || '')));
        setAdminUsers(list);
      },
      () => setAdminUsers([])
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'squads')), (snap) => {
      const list = snap.docs.map(d => ({ ...(d.data() as any), id: d.id } as Squad));
      list.sort((a, b) => String((a as any)?.title || '').localeCompare(String((b as any)?.title || '')));
      setSquads(list);
    });
    return () => unsub();
  }, []);

  useEffect(() => onSnapshot(collection(db, 'partners'), (snap) => {
    setPartners(snap.docs.map((item) => ({ id: item.id, ...(item.data() as any) })));
  }, () => setPartners([])), []);

  useEffect(() => onSnapshot(collection(db, 'locations'), (snap) => {
    setLocations(snap.docs.map((item) => ({ id: item.id, ...(item.data() as any) })));
  }, () => setLocations([])), []);

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'opsTemplates')), (snap) => {
      const list = snap.docs.map(d => ({ ...(d.data() as any), templateId: d.id } as OpsTemplate));
      list.sort((a, b) => String(a.title || a.templateId).localeCompare(String(b.title || b.templateId)));
      setTemplates(list);
    }, () => {
      setTemplates([]);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'opsCheckpoints')), (snap) => {
      const list = snap.docs.map(d => ({ ...(d.data() as any), checkpointId: d.id } as OpsCheckpointDefinition));
      list.sort((a, b) => String(a.title || a.checkpointId).localeCompare(String(b.title || b.checkpointId)));
      setCheckpointDefs(list);
    }, () => setCheckpointDefs([]));
    return () => unsub();
  }, []);

  const squadById = useMemo(() => {
    const m = new Map<string, Squad>();
    squads.forEach(s => m.set(String((s as any).id), s));
    return m;
  }, [squads]);

  const partnerById = useMemo(() => new Map(partners.map((item) => [String(item.id), item])), [partners]);
  const locationById = useMemo(() => new Map(locations.map((item) => [String(item.id), item])), [locations]);

  const rows: FlowRow[] = useMemo(() => {
    const needle = safeString(queryText).toLowerCase();
    const out: FlowRow[] = [];
    for (const f of flows) {
      const squadId = String(f.squadId || f.squadIds?.[0] || '');
      const sq = squadById.get(squadId);
      const partnerId = String(f.partnerId || (sq as any)?.partnerId || '');
      const identity = resolveActiveFlowIdentity(f as any, partnerById, locationById);
      const squadTitle = String((sq as any)?.title || `${(f as any).squadIds?.length || 0} squads`);
      const partnerName = String(
        f.readOnlyHistorical
          ? ((f as any).schoolName || (f as any).schoolId || 'Historical school')
          : identity.partnerName
      );
      const locationNames = identity.locationNames;
      const hit = !needle
        || squadTitle.toLowerCase().includes(needle)
        || partnerName.toLowerCase().includes(needle)
        || locationNames.some((name) => name.toLowerCase().includes(needle))
        || squadId.toLowerCase().includes(needle);
      if (!hit) continue;
      out.push({
        id: f.id,
        squadTitle,
        partnerName,
        locationNames,
        updatedAt: f.updatedAt,
      });
    }
    // Stable sorting: most recently updated first
    out.sort((a, b) => Number(b.updatedAt || 0) - Number(a.updatedAt || 0));
    return out;
  }, [flows, locationById, partnerById, queryText, squadById]);

  const selectedFlow: OpsFlow | null = useMemo(() => {
    const id = selectedFlowId;
    if (!id) return null;
    return flows.find(f => f.id === id) || null;
  }, [flows, selectedFlowId]);

  const logisticsById = useMemo(
    () => selectedFlow?.readOnlyHistorical ? LEGACY_OPS_LOGISTICS_BY_ID : OPS_LOGISTICS_BY_ID,
    [selectedFlow?.readOnlyHistorical],
  );

  useEffect(() => {
    if (!selectedFlow?.readOnlyHistorical) {
      setLegacySchool(null);
      return;
    }
    const schoolId = String(selectedFlow.schoolId || '').trim();
    if (!schoolId) {
      setLegacySchool(null);
      return;
    }
    let active = true;
    void getDoc(doc(db, 'schools', schoolId)).then((snapshot) => {
      if (!active) return;
      setLegacySchool(snapshot.exists() ? ({ id: snapshot.id, ...(snapshot.data() as any) } as School) : null);
    });
    return () => { active = false; };
  }, [selectedFlow?.id, selectedFlow?.readOnlyHistorical, selectedFlow?.schoolId]);

  useEffect(() => {
    // Keep template-update UI state scoped to the current flow.
    setUpdateTemplateError(null);
    setUpdateTemplateErrorIdx(null);
    setUpdatingTemplateIdx(null);
  }, [selectedFlowId]);

  const selectedSquad: Squad | null = useMemo(() => {
    if (!selectedFlow) return null;
    const squadId = String(selectedFlow.squadId || selectedFlow.squadIds?.[0] || '');
    return squadById.get(squadId) || null;
  }, [selectedFlow, squadById]);

  const selectedPartner = selectedFlow?.partnerId ? partnerById.get(selectedFlow.partnerId) || null : null;
  const selectedLocationId = String(selectedFlow?.locationIds?.[0] || (selectedSquad as any)?.locationId || '');
  const selectedLocation = selectedLocationId ? locationById.get(selectedLocationId) || null : null;

  useEffect(() => {
    setLogisticDraftById({});
    setLogisticDirtyById({});
    setLogisticSaveErrorById({});
  }, [selectedFlow?.id]);

  useEffect(() => {
    const partnerId = String(selectedFlow?.partnerId || '');
    const seasonId = String(selectedFlow?.seasonId || '');
    if (!partnerId || !seasonId) {
      setPartnerSeason({});
      return;
    }
    return onSnapshot(doc(db, 'partners', partnerId, 'seasons', seasonId), (snapshot) => {
      setPartnerSeason(snapshot.exists() ? { id: snapshot.id, ...(snapshot.data() as any) } : {});
    }, () => setPartnerSeason({}));
  }, [selectedFlow?.partnerId, selectedFlow?.seasonId]);

  const effectivePartnerId = useMemo(() => {
    return String((selectedFlow as any)?.partnerId || '').trim();
  }, [selectedFlow]);
  const effectiveLocationId = selectedLocationId;

  const setTeacherChampionOptional = async (isOptional: boolean) => {
    setDerivedEditError(null);
    const partnerId = effectivePartnerId;
    if (!partnerId) return;
    try {
      await updateDoc(doc(db, 'partners', partnerId), {
        teacherChampionOptional: Boolean(isOptional),
        updatedAt: Date.now(),
        updatedBy: String((userData as any)?.id || ''),
      } as any);
    } catch (e: any) {
      setDerivedEditError(String(e?.message || 'Failed to update partner.'));
    }
  };

  const percentComplete = useMemo(() => (selectedFlow ? computePercentComplete(selectedFlow) : 0), [selectedFlow]);
  const [riskDraft, setRiskDraft] = useState<string>('');
  const [editingPriority, setEditingPriority] = useState(false);
  const priorityInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!selectedFlow) {
      setRiskDraft('');
      setEditingPriority(false);
      return;
    }
    const v = (selectedFlow as any)?.riskScoreOutOf10;
    setRiskDraft(v === null || v === undefined ? '' : String(v));
  }, [selectedFlow]);

  useEffect(() => {
    if (!editingPriority) return;
    // Defer focus until after the input renders.
    const t = window.setTimeout(() => {
      priorityInputRef.current?.focus();
      priorityInputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(t);
  }, [editingPriority]);

  const saveRiskScore = async (nextRaw: string) => {
    if (!selectedFlow) return;
    const cleaned = String(nextRaw || '').trim();
    const n = cleaned === '' ? null : Math.max(0, Math.min(10, Number(cleaned)));
    if (cleaned !== '' && !Number.isFinite(Number(n))) return;
    try {
      const ref = doc(db, 'partnerSeasonFlows', selectedFlow.id);
      await updateDoc(ref, {
        riskScoreOutOf10: n,
        updatedAt: Date.now(),
        updatedBy: (userData as any)?.id || '',
      } as any);
    } catch (e: any) {
      setApplyError(String(e?.message || 'Failed to save risk score.'));
    }
  };

  const adminById = useMemo(() => {
    const m = new Map<string, User>();
    adminUsers.forEach(u => m.set(String((u as any)?.id || ''), u));
    return m;
  }, [adminUsers]);

  const adminOptions = useMemo(
    () => adminUsers.map(u => ({ id: String((u as any).id), label: String(u.displayName || u.email || (u as any).id) })),
    [adminUsers]
  );

  const saveInternalOwner = async (ownerId: string) => {
    if (!selectedFlow) return;
    try {
      const ref = doc(db, 'partnerSeasonFlows', selectedFlow.id);
      const now = Date.now();
      const actor = String((userData as any)?.id || '');
      await updateDoc(ref, {
        internalOwnerUserId: ownerId,
        updatedAt: now,
        updatedBy: actor,
      } as any);
    } catch (e: any) {
      setApplyError(String(e?.message || 'Failed to update internal owner.'));
    }
  };

  const savePipelineStage = async (toStage: OpsPipelineStageId, fromStage?: OpsPipelineStageId) => {
    if (!selectedFlow) return;
    if (fromStage && fromStage === toStage) return;
    const id = String(selectedFlow.id || '').trim();
    if (!id) return;
    setSavingStage(true);
    try {
      const now = Date.now();
      const actor = String((userData as any)?.id || '');
      await updateDoc(doc(db, 'partnerSeasonFlows', id), {
        pipelineStage: toStage,
        pipelineStageUpdatedAt: now,
        pipelineStageUpdatedBy: actor,
        updatedAt: now,
        updatedBy: actor,
      } as any);
      await addDoc(collection(db, 'partnerSeasonFlows', id, 'stageHistory'), {
        ...(fromStage ? { from: fromStage } : {}),
        to: toStage,
        movedAt: now,
        movedBy: actor,
      } as any);
    } catch (e: any) {
      setApplyError(String(e?.message || 'Failed to update stage.'));
    } finally {
      setSavingStage(false);
    }
  };

  const sections = useMemo(() => {
    if (!selectedFlow) return [];
    // Ensure state keys exist even if older docs are missing them.
    // Intake is no longer a mandatory section; only applied templates define checkpoints.
    let normalized: OpsFlow = selectedFlow as OpsFlow;
    const sources = Array.isArray((normalized as any)?.appliedSources) ? ((normalized as any).appliedSources as any[]) : [];
    for (const src of sources) {
      normalized = ensureFlowStateForSnapshot(normalized, (src as any)?.snapshot || null);
    }
    return buildFlowSections({ flow: normalized, hideSuperseded, includeIntake: false });
  }, [hideSuperseded, selectedFlow]);

  // Default behavior: when opening a flow for the first time (per session),
  // start with ALL checkpoints collapsed.
  useEffect(() => {
    const flowId = String((selectedFlow as any)?.id || '').trim();
    if (!flowId) return;
    if (collapsedInitializedFlowIdsRef.current.has(flowId)) return;

    const checkpointIds: string[] = [];
    for (const s of sections) {
      for (const row of s.checkpoints) checkpointIds.push(String(row.checkpoint.checkpointId || '').trim());
    }
    const unique = Array.from(new Set(checkpointIds.filter(Boolean)));
    if (unique.length === 0) return; // wait until checkpoints load

    const next: Record<string, boolean> = {};
    unique.forEach((id) => { next[id] = true; });
    setCollapsedCheckpointIds(next);
    collapsedInitializedFlowIdsRef.current.add(flowId);
  }, [sections, selectedFlow]);

  const checkpointProgressById = useMemo(() => {
    const byId: Record<string, { percent: number; ok: number; total: number }> = {};
    for (const s of sections) {
      for (const row of s.checkpoints) {
        const cp = row.checkpoint;
        const tasks = row.tasks || [];
        let total = 0;
        let ok = 0;
        for (const t of tasks) {
          const logisticId = String((t as any)?.logisticId || '').trim();
          if (!logisticId) continue;
          const def = logisticsById[logisticId] || null;
          if (!def) continue;
          let value: any = undefined;
          let derivedOk: boolean | undefined = undefined;
          if (def.scope === 'derived' && def.storage.kind === 'derived') {
            derivedOk = derivedOkByRuleId[def.storage.ruleId];
            value = derivedValueByRuleId[def.storage.ruleId];
          } else if (def.storage.kind === 'schools') {
            value = selectedFlow?.readOnlyHistorical
              ? getValueAtPath(legacySchool as any, def.storage.fieldPath)
              : undefined;
          } else if (def.storage.kind === 'billing_profile') {
            value = getValueAtPath(billingProfile as any, def.storage.fieldPath);
          } else if (def.storage.kind === 'partners') {
            value = getValueAtPath(selectedPartner as any, def.storage.fieldPath);
          } else if (def.storage.kind === 'locations') {
            value = getValueAtPath(selectedLocation as any, def.storage.fieldPath);
          } else if (def.storage.kind === 'partner_seasons') {
            value = getValueAtPath(partnerSeason as any, def.storage.fieldPath);
          } else if (def.storage.kind === 'squads') {
            value = getValueAtPath(selectedSquad as any, def.storage.fieldPath);
          }
          const marked = isLogisticMarkedNotNeeded(selectedFlow as any, logisticId);
          const isRemoved = marked && !def.allowNotApplicable;
          if (isRemoved) continue; // removed logistics do NOT count toward completion %
          total += 1;
          const notNeeded = def.allowNotApplicable ? marked : false;
          if (isCompleteByRule(def, value, derivedOk, notNeeded)) ok += 1;
        }
        const percent = total === 0 ? 100 : Math.round((ok / total) * 100);
        byId[String(cp.checkpointId)] = { percent, ok, total };
      }
    }
    return byId;
  }, [billingProfile, derivedOkByRuleId, derivedValueByRuleId, legacySchool, logisticsById, partnerSeason, sections, selectedFlow, selectedLocation, selectedPartner, selectedSquad]);

  const checkpointGateStatusById = useMemo(() => {
    // Status is derived from logistics completion + checkpoint order (gate behavior).
    // - If 100% -> complete
    // - ONLY the first (topmost) incomplete checkpoint -> in_progress
    // - All later incomplete checkpoints -> not_started
    //
    // Superseded checkpoints are treated as complete for gating.
    const orderedIds: string[] = [];
    for (const s of sections) {
      for (const row of s.checkpoints) orderedIds.push(String(row.checkpoint.checkpointId || '').trim());
    }
    const cleaned = orderedIds.filter(Boolean);
    const out: Record<string, { state: 'complete' | 'in_progress' | 'not_started'; percent: number }> = {};

    const cpStateById: any = (selectedFlow as any)?.checkpointState || {};
    const firstIncompleteId =
      cleaned.find((id) => {
        const st = String(cpStateById?.[id]?.status || '');
        if (st === 'superseded') return false;
        const pct = checkpointProgressById[id]?.percent ?? 0;
        return pct < 100;
      }) || null;

    for (let i = 0; i < cleaned.length; i++) {
      const id = cleaned[i];
      const pctRaw = checkpointProgressById[id]?.percent ?? 0;
      const st = String(cpStateById?.[id]?.status || '');
      const isSup = st === 'superseded';
      const pct = isSup ? 100 : pctRaw;
      const isComplete = pct >= 100;
      const isInProgress = !isComplete && firstIncompleteId === id;
      out[id] = { state: isComplete ? 'complete' : (isInProgress ? 'in_progress' : 'not_started'), percent: pct };
    }
    return out;
  }, [checkpointProgressById, sections, selectedFlow]);

  const overallLogisticsCompletion = useMemo(() => {
    let total = 0;
    let ok = 0;
    for (const v of Object.values(checkpointProgressById)) {
      total += v.total;
      ok += v.ok;
    }
    const percent = total === 0 ? 100 : Math.round((ok / total) * 100);
    return { percent, ok, total };
  }, [checkpointProgressById]);

  const checkpointMetaById = useMemo(() => {
    const out: Record<string, { title: string; order: number }> = {};
    let order = 0;
    for (const s of sections) {
      for (const row of s.checkpoints) {
        const id = String(row.checkpoint.checkpointId || '').trim();
        if (!id) continue;
        if (!out[id]) {
          out[id] = { title: String(row.checkpoint.title || id), order };
        }
        order += 1;
      }
    }
    return out;
  }, [sections]);

  const currentCheckpointSummary = useMemo(() => {
    // First checkpoint in progress (gate-derived); fallback to first checkpoint; otherwise null.
    const ordered: Array<{ id: string; title: string; dueYmd: string }> = [];
    for (const s of sections) {
      for (const row of s.checkpoints) {
        const id = String(row.checkpoint.checkpointId || '').trim();
        if (!id) continue;
        const title = String(row.checkpoint.title || id);
        const dueYmd = String((selectedFlow?.checkpointState || {})[id]?.dueDate || '').slice(0, 10);
        ordered.push({ id, title, dueYmd });
      }
    }
    if (ordered.length === 0) return null;
    const inProg = ordered.find(x => checkpointGateStatusById[x.id]?.state === 'in_progress') || ordered[0];
    const pctNum = checkpointGateStatusById[inProg.id]?.percent ?? checkpointProgressById[inProg.id]?.percent ?? 0;
    const due = inProg.dueYmd ? dueLabelFromYmd(inProg.dueYmd) : null;
    // Normalize to "Due in X days" phrasing when possible
    const dueText =
      due
        ? (due.text.startsWith('Due ') ? due.text.replace(/^Due\s+/i, 'Due ') : due.text)
        : 'No due date';
    return { ...inProg, percent: pctNum, dueText };
  }, [checkpointGateStatusById, checkpointProgressById, sections, selectedFlow?.checkpointState]);

  const resolvedPipelineStage: OpsPipelineStageId = useMemo(() => {
    if (!selectedFlow) return 'uncategorized';
    const anyInProgress = Object.values(checkpointGateStatusById).some((s: any) => s?.state === 'in_progress');
    const hasSections = sections.length > 0;
    return resolvePipelineStage(selectedFlow as any, {
      squadStatus: String((selectedSquad as any)?.status || ''),
      hasCurrentCheckpoint: anyInProgress,
      overallPercentComplete: hasSections ? overallLogisticsCompletion.percent : 0,
      allCheckpointsComplete: hasSections && !anyInProgress,
      currentCheckpointTitle: anyInProgress ? String(currentCheckpointSummary?.title || '') : '',
    });
  }, [selectedFlow, selectedSquad, sections.length, checkpointGateStatusById, overallLogisticsCompletion.percent, currentCheckpointSummary]);

  const effectiveFlowTodo: EffectiveFlowTodo = useMemo(() => {
    if (!selectedFlow) return { note: '', dueDateYmd: '', source: 'none' };

    const hasFlowTodoField = Object.prototype.hasOwnProperty.call((selectedFlow as any) || {}, 'flowTodo');
    if (hasFlowTodoField) {
      const ft: any = (selectedFlow as any)?.flowTodo || {};
      const note = normalizeTodoText(ft?.note || '');
      const dueDateYmd = String(ft?.dueDate || '').slice(0, 10);
      return { note, dueDateYmd, source: 'flow' };
    }

    const cpStateById: any = (selectedFlow as any)?.checkpointState || {};
    const candidates = Object.entries(cpStateById)
      .map(([checkpointId, stAny]: any) => {
        const note = normalizeTodoText(stAny?.note || '');
        const dueDateYmd = String(stAny?.dueDate || '').slice(0, 10);
        const meta = checkpointMetaById[String(checkpointId)] || { title: String(checkpointId), order: -1 };
        return {
          checkpointId: String(checkpointId),
          checkpointTitle: meta.title,
          order: meta.order,
          note,
          dueDateYmd,
        };
      })
      .filter((x: any) => Boolean(String(x.note || '').trim()) || Boolean(String(x.dueDateYmd || '').trim()));

    if (candidates.length === 0) return { note: '', dueDateYmd: '', source: 'none' };

    // Transition behavior: pick a single "most recent" legacy TODO.
    // Prefer checkpoints that actually have a TODO note; within that, prefer latest dueDate.
    const withNote = candidates.filter((x: any) => Boolean(String(x.note || '').trim()));
    const pool = withNote.length > 0 ? withNote : candidates;
    pool.sort((a: any, b: any) => {
      const ad = String(a.dueDateYmd || '');
      const bd = String(b.dueDateYmd || '');
      if (ad !== bd) return ad.localeCompare(bd);
      return Number(a.order ?? 0) - Number(b.order ?? 0);
    });
    const best = pool[pool.length - 1];
    return {
      note: String(best.note || ''),
      dueDateYmd: String(best.dueDateYmd || ''),
      source: 'checkpoint',
      checkpointId: String(best.checkpointId || ''),
      checkpointTitle: String(best.checkpointTitle || best.checkpointId || ''),
    };
  }, [checkpointMetaById, selectedFlow, selectedFlow?.checkpointState]);

  const missingLogisticsByCheckpointId = useMemo(() => {
    const out: Record<string, Array<{ logisticId: string; title: string; required: boolean; order: number }>> = {};
    if (!selectedFlow) return out;
    const cpStateById: any = (selectedFlow as any)?.checkpointState || {};
    for (const s of sections) {
      for (const row of s.checkpoints) {
        const cpId = String(row.checkpoint.checkpointId || '').trim();
        if (!cpId) continue;
        const st = String(cpStateById?.[cpId]?.status || '');
        if (st === 'superseded') continue;
        const tasks = Array.isArray(row.tasks) ? row.tasks : [];
        const arr: Array<{ logisticId: string; title: string; required: boolean; order: number }> = [];
        for (const t of tasks) {
          const taskState = (selectedFlow.taskState || {})[String((t as any)?.taskId || '')] || { status: 'open' };
          if (taskState.status === 'superseded') continue;
          const logisticId = String((t as any)?.logisticId || '').trim();
          if (!logisticId) continue;
          const def = logisticsById[logisticId] || null;
          if (!def) continue;
          let value: any = undefined;
          let derivedOk: boolean | undefined = undefined;
          if (def.scope === 'derived' && def.storage.kind === 'derived') {
            derivedOk = derivedOkByRuleId[def.storage.ruleId];
            value = derivedValueByRuleId[def.storage.ruleId];
          } else if (def.storage.kind === 'schools') {
            value = selectedFlow?.readOnlyHistorical
              ? getValueAtPath(legacySchool as any, def.storage.fieldPath)
              : undefined;
          } else if (def.storage.kind === 'billing_profile') {
            value = getValueAtPath(billingProfile as any, def.storage.fieldPath);
          } else if (def.storage.kind === 'partners') {
            value = getValueAtPath(selectedPartner as any, def.storage.fieldPath);
          } else if (def.storage.kind === 'locations') {
            value = getValueAtPath(selectedLocation as any, def.storage.fieldPath);
          } else if (def.storage.kind === 'partner_seasons') {
            value = getValueAtPath(partnerSeason as any, def.storage.fieldPath);
          } else if (def.storage.kind === 'squads') {
            value = getValueAtPath(selectedSquad as any, def.storage.fieldPath);
          }
          const marked = isLogisticMarkedNotNeeded(selectedFlow as any, logisticId);
          const isRemoved = marked && !def.allowNotApplicable;
          if (isRemoved) continue; // removed logistics should not appear as "missing"
          const notNeeded = def.allowNotApplicable ? marked : false;
          const complete = isCompleteByRule(def, value, derivedOk, notNeeded);
          if (!complete) {
            arr.push({
              logisticId,
              title: String((t as any)?.title || def.label || logisticId),
              required: Boolean((t as any)?.required),
              order: Number((t as any)?.order ?? 0),
            });
          }
        }
        out[cpId] = arr;
      }
    }
    return out;
  }, [billingProfile, derivedOkByRuleId, derivedValueByRuleId, legacySchool, logisticsById, partnerSeason, sections, selectedFlow, selectedLocation, selectedPartner, selectedSquad]);

  const missingLogisticsInCurrentCheckpoint = useMemo(() => {
    const cpId = String(currentCheckpointSummary?.id || '').trim();
    if (!cpId) return [];
    return missingLogisticsByCheckpointId[cpId] || [];
  }, [currentCheckpointSummary?.id, missingLogisticsByCheckpointId]);

  const missingLogisticsInFlow = useMemo(() => {
    const m = new Map<string, { logisticId: string; title: string; required: boolean; order: number }>();
    for (const list of Object.values(missingLogisticsByCheckpointId)) {
      for (const item of list) {
        if (!m.has(item.logisticId)) m.set(item.logisticId, item);
      }
    }
    return Array.from(m.values()).sort((a, b) => a.title.localeCompare(b.title));
  }, [missingLogisticsByCheckpointId]);

  const openOutreachPanel = () => {
    setOutreachChannel('email');
    setOutreachIncludeOtherMissing(false);
    setOutreachSelectedLogisticIds(missingLogisticsInCurrentCheckpoint.map(x => x.logisticId));
    setOutreachPanelOpen(true);
  };

  const closeTodoEditor = useCallback(() => {
    setTodoEditor(null);
    setTodoError(null);
    setTodoSaving(false);
    window.setTimeout(() => {
      try {
        todoReturnFocusRef.current?.focus?.();
      } catch {
        // ignore
      }
    }, 0);
  }, []);

  const openFlowTodoEditor = useCallback((anchorEl: HTMLElement) => {
    if (!selectedFlow) return;
    const rect = anchorEl.getBoundingClientRect();
    todoReturnFocusRef.current = anchorEl;

    const isMobile = (() => {
      try {
        return typeof window !== 'undefined'
          && (window.matchMedia?.('(max-width: 640px)')?.matches ?? window.innerWidth < 640);
      } catch {
        return true;
      }
    })();

    const hasFlowTodoField = Object.prototype.hasOwnProperty.call((selectedFlow as any) || {}, 'flowTodo');
    const ft: any = hasFlowTodoField ? ((selectedFlow as any)?.flowTodo || {}) : null;

    const seedNote = hasFlowTodoField
      ? String(ft?.note || '')
      : String(effectiveFlowTodo?.note || '');
    const seedDue = hasFlowTodoField
      ? String(ft?.dueDate || '').slice(0, 10)
      : String(effectiveFlowTodo?.dueDateYmd || '').slice(0, 10);

    setTodoEditor({
      target: 'flow',
      checkpointId: '',
      checkpointTitle: 'Flow TODO',
      anchorRect: { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height },
      isMobile,
      dueDateDraftYmd: seedDue,
      initialDueDateYmd: seedDue,
      draft: seedNote.slice(0, TODO_MAX_LEN),
      initialNormalized: normalizeTodoText(seedNote),
    });
    setTodoError(null);
    setTodoSaving(false);
  }, [effectiveFlowTodo?.dueDateYmd, effectiveFlowTodo?.note, selectedFlow]);

  useEffect(() => {
    if (!todoEditor) return;
    const t = window.setTimeout(() => {
      try {
        // Focus only once on open; do not select (selecting each render causes 1-char typing).
        todoTextareaRef.current?.focus?.();
      } catch {
        // ignore
      }
    }, 0);
    return () => window.clearTimeout(t);
  }, [todoEditor?.target]);

  useEffect(() => {
    if (!todoEditor) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeTodoEditor();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    if (!todoEditor.isMobile) {
      const onScrollOrResize = () => closeTodoEditor();
      window.addEventListener('scroll', onScrollOrResize, true);
      window.addEventListener('resize', onScrollOrResize);
      return () => {
        window.removeEventListener('keydown', onKeyDown);
        window.removeEventListener('scroll', onScrollOrResize, true);
        window.removeEventListener('resize', onScrollOrResize);
      };
    }
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [closeTodoEditor, todoEditor?.isMobile, todoEditor?.target]);

  const applyOutreach = async () => {
    if (!selectedFlow) return;
    const now = Date.now();
    const actor = String((userData as any)?.id || '');
    const channel = outreachChannel;
    const selectedIds = Array.from(new Set(outreachSelectedLogisticIds.map(s => String(s || '').trim()).filter(Boolean)));

    const prevMap: Record<string, any> = ((selectedFlow as any)?.logisticAttemptMetaByKey || {}) as any;
    const nextMap: Record<string, any> = { ...prevMap };
    for (const logisticId of selectedIds) {
      const key = logisticKeyFromId(logisticId);
      nextMap[key] = {
        logisticId,
        lastAttemptAt: now,
        lastAttemptBy: actor,
        lastAttemptChannel: channel,
        nextFollowUpAt: followUpAtForChannel(now, channel),
      };
    }

    try {
      const ref = doc(db, 'partnerSeasonFlows', selectedFlow.id);
      await updateDoc(ref, {
        lastOutreachAt: now,
        lastOutreachBy: actor,
        lastOutreachChannel: channel,
        logisticAttemptMetaByKey: nextMap,
        updatedAt: now,
        updatedBy: actor,
      } as any);
      setOutreachPanelOpen(false);
    } catch (e: any) {
      setApplyError(String(e?.message || 'Failed to log outreach.'));
    }
  };

  const toggleLogisticNotNeeded = async (logisticId: string, markNotNeeded: boolean) => {
    if (!selectedFlow) return;
    const id = String(logisticId || '').trim();
    if (!id) return;
    const now = Date.now();
    const actor = String((userData as any)?.id || '');
    const key = logisticKeyFromId(id);
    const prev: Record<string, any> = ((selectedFlow as any)?.logisticNotNeededByKey || {}) as any;
    const next: Record<string, any> = { ...prev };
    if (markNotNeeded) {
      next[key] = { logisticId: id, markedAt: now, markedBy: actor };
    } else {
      delete next[key];
    }
    try {
      const ref = doc(db, 'partnerSeasonFlows', selectedFlow.id);
      await updateDoc(ref, {
        logisticNotNeededByKey: Object.keys(next).length > 0 ? next : deleteField(),
        updatedAt: now,
        updatedBy: actor,
      } as any);
    } catch (e: any) {
      setApplyError(String(e?.message || 'Failed to update logistic.'));
    }
  };

  const copyTextToClipboard = async (text: string): Promise<boolean> => {
    try {
      const nav: any = navigator as any;
      if (nav?.clipboard?.writeText) {
        await nav.clipboard.writeText(text);
        return true;
      }
    } catch {
      // fall back below
    }
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.position = 'fixed';
      el.style.left = '-9999px';
      el.style.top = '0';
      document.body.appendChild(el);
      el.focus();
      el.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      return ok;
    } catch {
      return false;
    }
  };

  const showCopyToast = (msg: string) => {
    setCopyToast(msg);
    window.setTimeout(() => setCopyToast(null), 1600);
  };

  const deleteCurrentFlow = async () => {
    if (!selectedFlow) return;
    if (!isAdmin) return;
    if (deletingFlow) return;

    const flowId = safeString(selectedFlow.id);
    if (!flowId) return;

    const squadTitle = safeString((selectedSquad as any)?.title);
    const confirmName = squadTitle || flowId;
    const ok = await window.appConfirm(
      [
        `Delete this flow?`,
        ``,
        `Flow: ${confirmName}`,
        `Doc id: ${flowId}`,
        ``,
        `This deletes the partner-season flow document. It does NOT delete linked squads, partners, or locations.`,
        `This cannot be undone.`,
      ].join('\n')
    );
    if (!ok) return;

    setDeleteFlowError(null);
    setDeletingFlow(true);
    try {
      await deleteDoc(doc(db, 'partnerSeasonFlows', flowId));
      showCopyToast('Flow deleted.');

      // If this tab is used as a detail page, return to the list route.
      if (props.showBackToList) {
        navigate('/app/admin/ops/flows');
      } else {
        // Otherwise, clear selection so the list can re-select another flow.
        setSelectedFlowId('');
      }
    } catch (e: any) {
      setDeleteFlowError(String(e?.message || 'Failed to delete flow.'));
    } finally {
      setDeletingFlow(false);
    }
  };

  const saveSquadTitle = async (rawTitle: string) => {
    if (!selectedFlow) return;
    if (!isAdmin) return;
    if (savingSquadTitle) return;

    const squadId = safeString(selectedFlow.squadId);
    const nextTitle = safeString(rawTitle);
    if (!squadId) return;
    if (!nextTitle) {
      setSquadTitleError('Squad name cannot be empty.');
      return;
    }

    const curTitle = safeString((selectedSquad as any)?.title || '');
    if (curTitle && curTitle === nextTitle) {
      setEditingSquadTitle(false);
      setSquadTitleError(null);
      return;
    }

    setSavingSquadTitle(true);
    setSquadTitleError(null);
    try {
      await updateDoc(doc(db, 'squads', squadId), {
        title: nextTitle,
        updatedAt: Timestamp.now(),
      } as any);
      showCopyToast('Squad name updated.');
      setEditingSquadTitle(false);
    } catch (e: any) {
      setSquadTitleError(String(e?.message || 'Failed to update squad name.'));
    } finally {
      setSavingSquadTitle(false);
    }
  };

  const setEnrollmentMeetsMinOverride = async (next: boolean | null) => {
    if (!selectedFlow) return;
    if (!isAdmin) return;
    const squadId = safeString(selectedFlow.squadId);
    if (!squadId) return;
    setDerivedEditError(null);
    try {
      await updateDoc(doc(db, 'squads', squadId), {
        enrollmentMeetsMinOverride: next === null ? deleteField() : next,
        updatedAt: Timestamp.now(),
      } as any);
    } catch (e: any) {
      setDerivedEditError(String(e?.message || 'Failed to update enrollment override.'));
    }
  };

  const buildMissingLogisticsEmail = (params: {
    schoolName: string;
    programName?: string;
    checkpointName: string;
    contactName?: string;
    senderName: string;
    missing: Array<{ title: string; required: boolean; order: number }>;
  }): string => {
    const schoolName = safeString(params.schoolName);
    const programName = safeString(params.programName) || 'our program';
    const checkpointName = safeString(params.checkpointName) || 'this checkpoint';
    const contactName = safeString(params.contactName);
    const senderName = safeString(params.senderName) || '—';
    const missing = (params.missing || []).slice().sort((a, b) => {
      if (Boolean(a.required) !== Boolean(b.required)) return a.required ? -1 : 1;
      const ao = Number.isFinite(Number(a.order)) ? Number(a.order) : 0;
      const bo = Number.isFinite(Number(b.order)) ? Number(b.order) : 0;
      if (ao !== bo) return ao - bo;
      return String(a.title || '').localeCompare(String(b.title || ''));
    });

    const subjectSchool = schoolName || 'School';
    const subject = `Subject: ${subjectSchool} — ${checkpointName} quick follow-up`;
    const hi = contactName ? `Hi ${contactName},` : 'Hi there,';
    const atSchool = schoolName ? ` at ${schoolName}` : '';
    const intro = `Quick follow-up to keep ${programName} on track${atSchool}. When you have a moment, could you please confirm:`;
    const bullets = missing.map(x => `• ${String(x.title || '').trim()}`).join('\n');
    return [subject, '', hi, '', intro, '', bullets, '', 'Thank you!', senderName].join('\n');
  };

  const allCheckpointIds = useMemo(() => {
    const out = new Set<string>();
    for (const s of sections) {
      for (const row of s.checkpoints) out.add(String(row.checkpoint.checkpointId || '').trim());
    }
    return out;
  }, [sections]);

  // Includes superseded/hidden checkpoints (source-of-truth: snapshots).
  const allCheckpointIdsFromSnapshots = useMemo(() => {
    const out = new Set<string>();
    if (!selectedFlow) return out;
    const sources = Array.isArray((selectedFlow as any)?.appliedSources) ? ((selectedFlow as any).appliedSources as any[]) : [];
    for (const src of sources) {
      const cps = Array.isArray(src?.snapshot?.checkpoints) ? (src.snapshot.checkpoints as any[]) : [];
      for (const cp of cps) out.add(String(cp?.checkpointId || '').trim());
    }
    out.delete('');
    return out;
  }, [selectedFlow]);

  const checkpointCatalog = useMemo(() => {
    const byId = new Map<string, any>();
    checkpointDefs.forEach((c) => byId.set(String(c.checkpointId), c));
    const options = checkpointDefs
      .map((c) => ({ id: c.checkpointId, label: `${c.title || c.checkpointId} — ${c.checkpointId}` }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return { byId, options };
  }, [checkpointDefs]);

  // Keep canonical root partner billingDefaults in sync.
  useEffect(() => {
    const partnerId = effectivePartnerId;
    if (!partnerId) {
      setDerivedOkByRuleId({});
      setBillingProfile({});
      return;
    }
    let alive = true;
    const partnerRef = doc(db, 'partners', partnerId);

    const unsub = onSnapshot(
      partnerRef,
      (snap) => {
        const raw = snap.exists() ? ((snap.data() as any)?.billingDefaults || {}) : {};
        const data = expandDottedTopLevelKeys(raw) || {};
        setBillingProfile(data);

        const ids = Array.isArray(data?.authorizedContactIds) ? (data.authorizedContactIds as any[]) : [];
        const cleaned = ids.map((x: any) => String(x || '').trim()).filter(Boolean);
        setBillingAuthorizedContactIds(cleaned);

        const base: Record<string, boolean> = {
          billingProfileExists: Object.keys(data).length > 0,
          approvedBillingRecipientExists: cleaned.length > 0,
        };
        if (!alive) return;
        // Merge to avoid clobbering squad-derived keys like `enrollmentMeetsMin`.
        setDerivedOkByRuleId(prev => ({ ...prev, ...base }));
      },
      () => {
        setBillingProfile({});
        setBillingAuthorizedContactIds([]);
        setDerivedOkByRuleId(prev => ({
          ...prev,
          billingProfileExists: false,
          approvedBillingRecipientExists: false,
        }));
      }
    );

    return () => {
      alive = false;
      unsub();
    };
  }, [effectivePartnerId]);

  // Derived: contacts exist / flags on contacts (computed from in-memory contact list so it's always fresh).
  useEffect(() => {
    const optionalChampion = Boolean((selectedPartner as any)?.teacherChampionOptional);
    const applicableContacts = partnerContacts.filter((contact: any) => {
      const ids = Array.isArray(contact?.locationIds) ? contact.locationIds.map(String).filter(Boolean) : [];
      return ids.length === 0 || (!!effectiveLocationId && ids.includes(effectiveLocationId));
    });
    const hasRoleOrTag = (roleOrTag: string) => applicableContacts.some(c => {
      const role = String((c as any)?.role || '').trim();
      if (role === roleOrTag) return true;
      const tagsRaw = (c as any)?.tags;
      const tags = Array.isArray(tagsRaw) ? tagsRaw.map((t: any) => String(t || '').trim()).filter(Boolean) : [];
      return tags.includes(roleOrTag);
    });
    const hasFlag = (flag: string) => applicableContacts.some(c => (c as any)?.[flag] === true);

    setDerivedOkByRuleId(prev => ({
      ...prev,
      financeContactExists: hasRoleOrTag('finance'),
      schoolAfterschoolDirectorExists: hasRoleOrTag('afterschool_director'),
      schoolTeacherChampionExists: optionalChampion || hasRoleOrTag('teacher_champion'),
      schoolDecisionMakerExists: hasFlag('isDecisionMaker'),
      schoolPrimaryContactExists: hasFlag('isPrimaryContact'),
    }));
  }, [effectiveLocationId, partnerContacts, (selectedPartner as any)?.teacherChampionOptional]);

  // Derived logistic computed from the selected squad (not school).
  useEffect(() => {
    // Only meaningful when a flow/squad is selected.
    if (!selectedSquad) {
      setDerivedOkByRuleId(prev => ({ ...prev, enrollmentMeetsMin: false }));
      setDerivedValueByRuleId(prev => ({ ...prev, enrollmentMeetsMin: null }));
      setDerivedOkByRuleId(prev => ({ ...prev, squadHalfwayStatus: false, squadHalfwayNeedsIntervention: false }));
      setDerivedValueByRuleId(prev => ({ ...prev, squadHalfwayStatus: null, squadHalfwayNeedsIntervention: null }));
      return;
    }
    const minEnrollRaw = (selectedSquad as any)?.minEnroll;
    const minEnroll = Number(minEnrollRaw);
    const seatsTakenRaw = (selectedSquad as any)?.seatsTaken;
    const seatsTaken = Number(seatsTakenRaw);
    const fallbackEnrollment = Array.isArray((selectedSquad as any)?.studentIds) ? ((selectedSquad as any).studentIds as any[]).length : 0;
    // Prefer seatsTaken when present, but fall back to studentIds length to avoid false "No"
    // when seatsTaken isn't being maintained for older squads.
    const enrollment = Number.isFinite(seatsTaken)
      ? Math.max(Math.max(0, Math.trunc(seatsTaken)), Math.max(0, Math.trunc(fallbackEnrollment)))
      : fallbackEnrollment;
    const overrideRaw = (selectedSquad as any)?.enrollmentMeetsMinOverride;
    const override =
      typeof overrideRaw === 'boolean'
        ? overrideRaw
        : null;
    const computed =
      Number.isFinite(minEnroll) && minEnroll > 0
        ? enrollment >= minEnroll
        : null;
    const effective = override !== null ? override : computed;
    setDerivedValueByRuleId(prev => ({ ...prev, enrollmentMeetsMin: effective }));
    // For gating/completion semantics, only "Yes" counts as complete.
    setDerivedOkByRuleId(prev => ({ ...prev, enrollmentMeetsMin: effective === true }));

    const halfway: any = (selectedSquad as any)?.halfway || {};
    const engagement = Number(halfway?.engagementScore);
    const teacherPerf = Number(halfway?.teacherPerformanceScore);
    const schoolSat = Number(halfway?.schoolSatisfactionScore);
    const attendanceTrend = String(halfway?.attendanceTrend || '').trim();
    const pacingStatus = String(halfway?.pacingStatus || '').trim();
    const techIssue = String(halfway?.techIssueStatus || '').trim();
    const renewal = String(halfway?.renewalLikelihood || '').trim();

    const hasAllHalfwayInputs =
      Number.isFinite(engagement) && engagement >= 1 &&
      Number.isFinite(teacherPerf) && teacherPerf >= 1 &&
      Number.isFinite(schoolSat) && schoolSat >= 1 &&
      Boolean(attendanceTrend) &&
      Boolean(pacingStatus) &&
      Boolean(techIssue) &&
      Boolean(renewal);

    if (!hasAllHalfwayInputs) {
      setDerivedOkByRuleId(prev => ({ ...prev, squadHalfwayStatus: false, squadHalfwayNeedsIntervention: false }));
      setDerivedValueByRuleId(prev => ({ ...prev, squadHalfwayStatus: null, squadHalfwayNeedsIntervention: null }));
      return;
    }

    const warnings: string[] = [];
    const criticals: string[] = [];
    if (techIssue === 'blocking') criticals.push('tech.blocking');
    else if (techIssue === 'minor') warnings.push('tech.minor');

    if (renewal === 'no') criticals.push('renewal.no');
    else if (renewal === 'maybe') warnings.push('renewal.maybe');

    if (engagement <= 2) criticals.push('engagement.low');
    else if (engagement === 3) warnings.push('engagement.mid');

    if (teacherPerf <= 2) criticals.push('teacherPerf.low');
    else if (teacherPerf === 3) warnings.push('teacherPerf.mid');

    if (schoolSat <= 2) criticals.push('schoolSat.low');
    else if (schoolSat === 3) warnings.push('schoolSat.mid');

    if (attendanceTrend === 'down') warnings.push('attendance.down');
    if (pacingStatus === 'behind') warnings.push('pacing.behind');

    const status: 'green' | 'yellow' | 'red' =
      criticals.length > 0 ? 'red' : (warnings.length > 0 ? 'yellow' : 'green');

    const needsIntervention =
      status === 'red'
        ? true
        : status === 'yellow'
          ? (warnings.length >= 2) // multiple yellow signals -> intervention
          : false;

    setDerivedOkByRuleId(prev => ({ ...prev, squadHalfwayStatus: true, squadHalfwayNeedsIntervention: true }));
    setDerivedValueByRuleId(prev => ({ ...prev, squadHalfwayStatus: status, squadHalfwayNeedsIntervention: needsIntervention }));
  }, [selectedSquad]);

  // Load partner contacts and apply locationIds in active role resolution.
  useEffect(() => {
    const partnerId = effectivePartnerId;
    if (!partnerId) {
      setPartnerContacts([]);
      return;
    }
    const unsub = onSnapshot(
      collection(db, 'partners', partnerId, 'contacts'),
      (snap) => {
        const list = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as SchoolContactWithId[];
        list.sort((a, b) => String(a.displayName || '').localeCompare(String(b.displayName || '')));
        setPartnerContacts(list);
      },
      () => {
        setPartnerContacts([]);
      }
    );
    return () => unsub();
  }, [effectivePartnerId]);

  const applicablePartnerContacts = useMemo(
    () => partnerContacts.filter((contact: any) => {
        const ids = Array.isArray(contact?.locationIds) ? contact.locationIds.map(String).filter(Boolean) : [];
        return ids.length === 0 || (!!effectiveLocationId && ids.includes(effectiveLocationId));
      }),
    [effectiveLocationId, partnerContacts],
  );

  const contactOptions = useMemo(
    () => applicablePartnerContacts.map(c => ({
      id: c.id,
      label: `${String(c.displayName || '').trim() || '(no name)'}${c.email ? ` — ${c.email}` : ''}`,
    })),
    [applicablePartnerContacts]
  );

  const roleForPersonRuleId = (ruleId: string): ContactRole | null => {
    if (ruleId === 'schoolDecisionMakerExists') return 'principal';
    if (ruleId === 'schoolPrimaryContactExists') return 'primary';
    if (ruleId === 'schoolTeacherChampionExists') return 'teacher_champion';
    if (ruleId === 'schoolAfterschoolDirectorExists') return 'afterschool_director';
    if (ruleId === 'financeContactExists') return 'finance';
    return null;
  };

  // These are "tag" roles that can be assigned to any contact without changing their job title
  const isTagRole = (ruleId: string): boolean => {
    // NOTE: decision maker + primary contact are stored as boolean flags on the contact.
    // Finance + afterschool are stored as tags so we don't have to overwrite the contact's job title.
    return (
      ruleId === 'schoolDecisionMakerExists' ||
      ruleId === 'schoolPrimaryContactExists' ||
      ruleId === 'financeContactExists' ||
      ruleId === 'schoolAfterschoolDirectorExists'
    );
  };

  const tagForPersonRuleId = (ruleId: string): string | null => {
    if (ruleId === 'financeContactExists') return 'finance';
    if (ruleId === 'schoolAfterschoolDirectorExists') return 'afterschool_director';
    return null;
  };

  const contactHasTag = (c: any, tag: string): boolean => {
    const tagsRaw = c?.tags;
    const tags = Array.isArray(tagsRaw) ? tagsRaw.map((t: any) => String(t || '').trim()).filter(Boolean) : [];
    return tags.includes(tag);
  };

  // Find contact that has the tag role assigned
  const findContactWithTagRole = (ruleId: string): SchoolContactWithId | null => {
    if (ruleId === 'schoolDecisionMakerExists') {
      return applicablePartnerContacts.find(c => (c as any).isDecisionMaker === true) || null;
    }
    if (ruleId === 'schoolPrimaryContactExists') {
      return applicablePartnerContacts.find(c => (c as any).isPrimaryContact === true) || null;
    }
    const tag = tagForPersonRuleId(ruleId);
    if (tag) {
      // Prefer explicit tag over legacy role match.
      const tagged = applicablePartnerContacts.find(c => contactHasTag(c as any, tag)) || null;
      if (tagged) return tagged;
      return applicablePartnerContacts.find(c => String((c as any)?.role || '').trim() === tag) || null;
    }
    return null;
  };

  const labelForPersonRuleId = (ruleId: string): string => {
    if (ruleId === 'schoolDecisionMakerExists') return 'Decision maker';
    if (ruleId === 'schoolPrimaryContactExists') return 'Primary partner contact';
    if (ruleId === 'schoolTeacherChampionExists') return 'Teacher champion';
    if (ruleId === 'schoolAfterschoolDirectorExists') return 'Afterschool director';
    if (ruleId === 'financeContactExists') return 'Finance contact';
    return 'Contact';
  };

  const cleanedPhone = (raw: string) => String(raw || '').replace(/[^\d+]/g, '').trim();

  const ensurePersonDraft = (ruleId: string) => {
    setPersonDraftByRuleId(prev => {
      if (prev[ruleId]) return prev;
      return { ...prev, [ruleId]: { firstName: '', lastName: '', email: '', phone: '' } };
    });
  };

  const refreshPartnerContacts = async () => {
    const partnerId = effectivePartnerId;
    if (!partnerId) return;
    try {
      const snap = await getDocs(collection(db, 'partners', partnerId, 'contacts'));
      const list = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as SchoolContactWithId[];
      list.sort((a, b) => String(a.displayName || '').localeCompare(String(b.displayName || '')));
      setPartnerContacts(list);
    } catch {
      // ignore
    }
  };

  const assignExistingContactToRole = async (ruleId: string, contactId: string) => {
    setDerivedEditError(null);
    const partnerId = effectivePartnerId;
    if (!partnerId || !contactId) return;
    
    try {
      if (isTagRole(ruleId)) {
        const tag = tagForPersonRuleId(ruleId);
        if (tag) {
          // Tag roles: store as tags[] so we don't overwrite contact.role.
          const existingWithTag = applicablePartnerContacts.find(c => contactHasTag(c as any, tag) && c.id !== contactId) || null;
          if (existingWithTag) {
            const prevTags = Array.isArray((existingWithTag as any)?.tags)
              ? ((existingWithTag as any).tags as any[]).map((t: any) => String(t || '').trim()).filter(Boolean)
              : [];
            const nextTags = prevTags.filter(t => t !== tag);
            await updateDoc(doc(db, 'partners', partnerId, 'contacts', existingWithTag.id), {
              tags: nextTags,
              updatedAt: Date.now(),
            } as any);
          }
          const picked = partnerContacts.find(c => String(c.id) === String(contactId)) as any;
          const prevTags = Array.isArray(picked?.tags)
            ? (picked.tags as any[]).map((t: any) => String(t || '').trim()).filter(Boolean)
            : [];
          const nextTags = Array.from(new Set([...prevTags, tag]));
          await updateDoc(doc(db, 'partners', partnerId, 'contacts', contactId), {
            tags: nextTags,
            updatedAt: Date.now(),
          } as any);
        } else {
          // Flag roles (primary contact, decision maker): store as boolean flags.
          // First, clear the flag from any other contact that has it.
          const flagField = ruleId === 'schoolDecisionMakerExists' ? 'isDecisionMaker' : 'isPrimaryContact';
          const existingWithFlag = applicablePartnerContacts.find(c => (c as any)[flagField] === true);
          if (existingWithFlag && existingWithFlag.id !== contactId) {
            await updateDoc(doc(db, 'partners', partnerId, 'contacts', existingWithFlag.id), {
              [flagField]: false,
              updatedAt: Date.now(),
            } as any);
          }
          // Set the flag on the selected contact.
          await updateDoc(doc(db, 'partners', partnerId, 'contacts', contactId), {
            [flagField]: true,
            updatedAt: Date.now(),
          } as any);
        }
      } else {
        // For job title roles, set the role field
        const role = roleForPersonRuleId(ruleId);
        if (!role) return;
        await updateDoc(doc(db, 'partners', partnerId, 'contacts', contactId), {
          role,
          updatedAt: Date.now(),
        } as any);
      }
      setDerivedOkByRuleId(prev => ({ ...prev, [ruleId]: true }));
      await refreshPartnerContacts();
    } catch (e: any) {
      setDerivedEditError(String(e?.message || 'Failed to update contact.'));
    }
  };

  const createContactForRole = async (ruleId: string) => {
    setDerivedEditError(null);
    const partnerId = effectivePartnerId;
    if (!partnerId) return;
    const draft = personDraftByRuleId[ruleId] || { firstName: '', lastName: '', email: '', phone: '' };
    const firstName = String(draft.firstName || '').trim();
    const lastName = String(draft.lastName || '').trim();
    const displayName = `${firstName} ${lastName}`.trim();
    const email = String(draft.email || '').trim();
    const phone = cleanedPhone(String(draft.phone || '').trim());
    if (!displayName) {
      setDerivedEditError('First name + last name are required.');
      return;
    }
    try {
      const contactData: any = {
        displayName,
        // Always provide a role so downstream UIs don't break on missing data.
        role: 'other',
        ...(email ? { email } : {}),
        ...(phone ? { phone } : {}),
        ownerUserId: String((userData as any)?.id || ''),
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
      
      if (isTagRole(ruleId)) {
        const tag = tagForPersonRuleId(ruleId);
        if (tag) {
          contactData.tags = Array.from(new Set([...(Array.isArray(contactData.tags) ? contactData.tags : []), tag]));
        } else {
          // For flag roles, set the flag instead of role.
          const flagField = ruleId === 'schoolDecisionMakerExists' ? 'isDecisionMaker' : 'isPrimaryContact';
          contactData[flagField] = true;
          // Clear flag from any existing contact.
          const existingWithFlag = applicablePartnerContacts.find(c => (c as any)[flagField] === true);
          if (existingWithFlag) {
            await updateDoc(doc(db, 'partners', partnerId, 'contacts', existingWithFlag.id), {
              [flagField]: false,
              updatedAt: Date.now(),
            } as any);
          }
        }
      } else {
        // For job title roles, set the role field
        const role = roleForPersonRuleId(ruleId);
        if (!role) return;
        contactData.role = role;
      }
      
      await addDoc(collection(db, 'partners', partnerId, 'contacts'), {
        ...contactData,
        locationIds: effectiveLocationId ? [effectiveLocationId] : [],
      });
      setPersonDraftByRuleId(prev => ({ ...prev, [ruleId]: { firstName: '', lastName: '', email: '', phone: '' } }));
      setDerivedOkByRuleId(prev => ({ ...prev, [ruleId]: true }));
      await refreshPartnerContacts();
    } catch (e: any) {
      setDerivedEditError(String(e?.message || 'Failed to create contact.'));
    }
  };

  const updateCheckpoint = async (checkpointId: string, patch: Partial<{ status: OpsCheckpointStatus; dueDate: string | null; note: string | null }>) => {
    if (!selectedFlow) return;
    const ref = doc(db, 'partnerSeasonFlows', selectedFlow.id);
    const now = Date.now();
    // Use explicit overload calls (avoid spreading varargs; TS gets unhappy).
    if (patch.status) {
      await updateDoc(
        ref,
        new FieldPath('checkpointState', checkpointId, 'status') as any,
        patch.status,
        'updatedAt',
        now
      );
    }
    if ('dueDate' in patch) {
      await updateDoc(
        ref,
        new FieldPath('checkpointState', checkpointId, 'dueDate') as any,
        patch.dueDate || null,
        'updatedAt',
        now
      );
    }
    if ('note' in patch) {
      const normalized = patch.note == null ? null : normalizeTodoText(patch.note);
      await updateDoc(
        ref,
        new FieldPath('checkpointState', checkpointId, 'note') as any,
        normalized || null,
        'updatedAt',
        now
      );
    }
  };

  const saveTodoEditor = useCallback(async (opts?: { clear?: boolean }) => {
    if (!todoEditor) return;
    if (!selectedFlow) return;
    setTodoSaving(true);
    setTodoError(null);
    try {
      const now = Date.now();
      const actor = String((userData as any)?.id || '');

      if (todoEditor.target === 'flow') {
        const dueYmd = String(todoEditor.dueDateDraftYmd || '').slice(0, 10);
        const normalized = opts?.clear ? '' : normalizeTodoText(todoEditor.draft);
        const ref = doc(db, 'partnerSeasonFlows', selectedFlow.id);

        if (opts?.clear) {
          await updateDoc(ref, {
            flowTodo: {
              note: null,
              dueDate: null,
              updatedAt: now,
              updatedBy: actor,
            },
            updatedAt: now,
            updatedBy: actor,
          } as any);
          closeTodoEditor();
          return;
        }

        if (!normalized) {
          setTodoError('Please enter a TODO before saving.');
          setTodoSaving(false);
          return;
        }
        if (!dueYmd) {
          setTodoError('Please enter a due date before saving this TODO.');
          setTodoSaving(false);
          return;
        }

        await updateDoc(ref, {
          flowTodo: {
            note: normalized,
            dueDate: dueYmd,
            updatedAt: now,
            updatedBy: actor,
          },
          updatedAt: now,
          updatedBy: actor,
        } as any);
        closeTodoEditor();
        return;
      }

      // Legacy: checkpoint-specific TODOs (kept for backwards compatibility).
      const dueYmd = String(todoEditor.dueDateDraftYmd || '').slice(0, 10);
      if (!dueYmd) {
        setTodoError('Please enter a due date for this checkpoint before saving a TODO.');
        setTodoSaving(false);
        return;
      }
      const normalized = opts?.clear ? '' : normalizeTodoText(todoEditor.draft);
      await updateCheckpoint(todoEditor.checkpointId, { note: normalized ? normalized : null });
      closeTodoEditor();
    } catch (e: any) {
      setTodoError(String(e?.message || 'Failed to save TODO.'));
    } finally {
      setTodoSaving(false);
    }
  }, [closeTodoEditor, selectedFlow, todoEditor, userData]);

  const updateCheckpointOrderOverride = async (checkpointId: string, orderOverride: number | null) => {
    if (!selectedFlow) return;
    const ref = doc(db, 'partnerSeasonFlows', selectedFlow.id);
    await updateDoc(
      ref,
      new FieldPath('checkpointState', checkpointId, 'orderOverride') as any,
      orderOverride,
      'updatedAt',
      Date.now()
    );
  };

  const updateTask = async (taskId: string, status: OpsTaskStatus) => {
    if (!selectedFlow) return;
    const ref = doc(db, 'partnerSeasonFlows', selectedFlow.id);
    await updateDoc(
      ref,
      new FieldPath('taskState', taskId, 'status') as any,
      status,
      'updatedAt',
      Date.now()
    );
  };

  const saveLogisticValue = async (def: OpsLogisticDefinition, rawValue: any) => {
    if (!selectedFlow) return;
    if (def.storage.kind === 'derived') return;

    if (def.storage.kind === 'schools') {
      throw new Error('Historical school logistics are read-only.');
    }

    if (def.storage.kind === 'billing_profile') {
      throw new Error('Historical school billing logistics are read-only.');
    }

    const partnerId = String(selectedFlow.partnerId || '');
    if (def.storage.kind === 'partners') {
      if (!partnerId) throw new Error('No partnerId available for this flow.');
      const fieldPath = def.storage.fieldPath;
      await updateDoc(doc(db, 'partners', partnerId), {
        [fieldPath]: rawValue,
        updatedAt: Timestamp.now(),
      });
      setPartners((current) => current.map((partner) =>
        String(partner.id) === partnerId
          ? withValueAtPath(partner, fieldPath, rawValue)
          : partner
      ));
      return;
    }
    if (def.storage.kind === 'locations') {
      if (!selectedLocationId) throw new Error('No locationId available for this flow.');
      const fieldPath = def.storage.fieldPath;
      await updateDoc(doc(db, 'locations', selectedLocationId), {
        [fieldPath]: rawValue,
        updatedAt: Timestamp.now(),
      });
      setLocations((current) => current.map((location) =>
        String(location.id) === selectedLocationId
          ? withValueAtPath(location, fieldPath, rawValue)
          : location
      ));
      return;
    }
    if (def.storage.kind === 'partner_seasons') {
      if (!partnerId || !selectedFlow.seasonId) throw new Error('No partner-season available for this flow.');
      const fieldPath = def.storage.fieldPath;
      await updateDoc(doc(db, 'partners', partnerId, 'seasons', selectedFlow.seasonId), {
        [fieldPath]: rawValue,
        updatedAt: Timestamp.now(),
      });
      setPartnerSeason((current: any) => withValueAtPath(current, fieldPath, rawValue));
      return;
    }
    // squads
    const squadId = String(selectedFlow.squadId || selectedFlow.squadIds?.[0] || '');
    if (!squadId) throw new Error('No squadId available for this flow.');
    const fieldPath = def.storage.fieldPath;
    const ref = doc(db, 'squads', squadId);
    await updateDoc(ref, { [fieldPath]: rawValue } as any);
    setSquads((current) => current.map((squad) =>
      String((squad as any).id) === squadId
        ? withValueAtPath(squad, fieldPath, rawValue)
        : squad
    ));
    if (shouldSyncCalendarForSquadLogistic(fieldPath)) {
      const updatedSnap = await getDoc(ref);
      const updatedSquad = updatedSnap.exists()
        ? ({ ...(updatedSnap.data() as any), id: updatedSnap.id } as Squad)
        : null;
      if (squadHasCompleteCalendarSchedule(updatedSquad)) {
        await syncSquadCalendarFromDetails(updatedSquad as any);
      }
    }
  };

  const getLocalDraftKey = (def: OpsLogisticDefinition) =>
    `${String(selectedFlow?.id || 'no-flow')}::${def.id}`;

  const getDisplayStringForLogistic = (def: OpsLogisticDefinition, value: any): string => {
    if (def.id === 'squad.priceCents') {
      return value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value))
        ? (Number(value) / 100).toFixed(2)
        : '';
    }
    if (def.id === 'squad.tuitionPerStudentPerClassCents' || def.id === 'squad.flatFeePerClassCents') {
      return value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value))
        ? (Number(value) / 100).toFixed(2)
        : '';
    }
    if (def.type === 'contact_date') {
      if (!value || typeof value !== 'object') return '';
      const cid = String((value as any)?.contactId || '').trim();
      const date = String((value as any)?.date || '').trim();
      const contact = cid ? (applicablePartnerContacts.find(x => String(x.id) === cid) || null) : null;
      const name = contact ? (String(contact.displayName || '').trim() || cid) : cid;
      const parts = [name, date].filter(Boolean);
      return parts.join(' — ');
    }
    if (def.id === 'location.pocContactId' || (selectedFlow?.readOnlyHistorical && def.id === 'school.pocContactId')) {
      const cid = String(value || '').trim();
      if (!cid) return '';
      const c = applicablePartnerContacts.find(x => String(x.id) === cid) || null;
      if (!c) return cid;
      const name = String(c.displayName || '').trim() || '(no name)';
      const email = String((c as any)?.email || '').trim();
      const phone = String((c as any)?.phone || '').trim();
      const bits = [name, email, phone].filter(Boolean);
      return bits.join(' — ');
    }
    if (def.id === 'squad.firstDayTeacherPacket') {
      if (!value || typeof value !== 'object') return '';
      const sentAt = Number((value as any)?.sentAt || 0);
      const generatedAt = Number((value as any)?.latestPdf?.generatedAt || 0);
      if (sentAt) return `sent ${new Date(sentAt).toLocaleString()}`;
      if (generatedAt) return `generated ${new Date(generatedAt).toLocaleString()}`;
      return '';
    }
    if (def.type === 'time') {
      return formatTime24hTo12hLabel(String(value ?? ''));
    }
    if (def.type === 'number') return value === null || value === undefined ? '' : String(value);
    if (def.type === 'boolean') return value ? 'true' : 'false';
    if (def.type === 'string_list' || def.type === 'file_list' || def.type === 'date_list') {
      return Array.isArray(value) ? value.join('\n') : String(value ?? '');
    }
    return String(value ?? '');
  };

  const createAndAssignMainPoc = async () => {
    setMainPocCreateError(null);
    const partnerId = effectivePartnerId;
    if (!partnerId || !effectiveLocationId) {
      setMainPocCreateError('Partner and location are required for this flow.');
      return;
    }
    const firstName = String(mainPocDraft.firstName || '').trim();
    const lastName = String(mainPocDraft.lastName || '').trim();
    const displayName = `${firstName} ${lastName}`.trim();
    if (!displayName) {
      setMainPocCreateError('First name + last name are required.');
      return;
    }
    const email = String(mainPocDraft.email || '').trim();
    const phone = cleanedPhone(String(mainPocDraft.phone || '').trim());
    try {
      const contactData: any = {
        displayName,
        ...(email ? { email } : {}),
        ...(phone ? { phone } : {}),
        ownerUserId: String((userData as any)?.id || ''),
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
      const ref = await addDoc(collection(db, 'partners', partnerId, 'contacts'), {
        ...contactData,
        locationIds: [effectiveLocationId],
      });
      await refreshPartnerContacts();

      const def = logisticsById['location.pocContactId'];
      if (!def) throw new Error('Location POC logistic is unavailable.');
      const key = getLocalDraftKey(def);
      // Optimistically set local draft so the dropdown updates immediately.
      setLogisticDraftById(prev => ({ ...prev, [key]: ref.id }));
      setLogisticDirtyById(prev => ({ ...prev, [key]: true }));

      await saveLogisticValue(def, ref.id);
      setLogisticDirtyById(prev => ({ ...prev, [key]: false }));
      setLogisticSaveErrorById(prev => {
        const { [key]: _ignore, ...rest } = prev;
        return rest;
      });

      setMainPocDraft({ firstName: '', lastName: '', email: '', phone: '' });
      setMainPocCreateOpen(false);
    } catch (e: any) {
      setMainPocCreateError(String(e?.message || 'Failed to create contact.'));
    }
  };

  const createContactAndAssignToContactDate = async () => {
    setContactDateCreateError(null);
    const ctx = contactDateCreateCtx;
    const partnerId = effectivePartnerId;
    if (!ctx?.key || !ctx?.logisticId) return;
    if (!partnerId) {
      setContactDateCreateError('No partnerId available for this flow.');
      return;
    }
    const def = logisticsById[String(ctx.logisticId)] || null;
    if (!def || def.type !== 'contact_date') {
      setContactDateCreateError('This logistic is not a contact+date field.');
      return;
    }
    const firstName = String(contactDateDraft.firstName || '').trim();
    const lastName = String(contactDateDraft.lastName || '').trim();
    const displayName = `${firstName} ${lastName}`.trim();
    if (!displayName) {
      setContactDateCreateError('First name + last name are required.');
      return;
    }
    const email = String(contactDateDraft.email || '').trim();
    const phone = cleanedPhone(String(contactDateDraft.phone || '').trim());
    try {
      const contactData: any = {
        displayName,
        // Always provide a role so downstream UIs don't break on missing data.
        role: 'other',
        ...(email ? { email } : {}),
        ...(phone ? { phone } : {}),
        ownerUserId: String((userData as any)?.id || ''),
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      const ref = await addDoc(collection(db, 'partners', partnerId, 'contacts'), {
        ...contactData,
        locationIds: effectiveLocationId ? [effectiveLocationId] : [],
      });
      await refreshPartnerContacts();

      // Preserve any existing date already entered for this logistic.
      const existingRaw =
        Object.prototype.hasOwnProperty.call(logisticDraftById, ctx.key)
          ? logisticDraftById[ctx.key]
          : (() => {
              let v: any = undefined;
              if (def.scope === 'derived' && def.storage.kind === 'derived') {
                v = derivedValueByRuleId[def.storage.ruleId];
              } else if (def.storage.kind === 'schools') {
                v = selectedFlow?.readOnlyHistorical
                  ? getValueAtPath(legacySchool as any, def.storage.fieldPath)
                  : undefined;
              } else if (def.storage.kind === 'billing_profile') {
                v = getValueAtPath(billingProfile as any, def.storage.fieldPath);
              } else if (def.storage.kind === 'squads') {
                v = getValueAtPath(selectedSquad as any, def.storage.fieldPath);
              }
              return v;
            })();

      const obj = (existingRaw && typeof existingRaw === 'object') ? existingRaw : {};
      const existingDate = String((obj as any)?.date || '').trim().slice(0, 10);
      const next = { contactId: ref.id, date: existingDate };

      setLogisticDraftById(prev => ({ ...prev, [ctx.key]: next }));
      setLogisticDirtyById(prev => ({ ...prev, [ctx.key]: true }));
      await commitLogisticDraft(def, next);

      setContactDateDraft({ firstName: '', lastName: '', email: '', phone: '' });
      setContactDateCreateCtx(null);
    } catch (e: any) {
      setContactDateCreateError(String(e?.message || 'Failed to create contact.'));
    }
  };

  const commitLogisticDraft = async (def: OpsLogisticDefinition, rawOverride?: any) => {
    const key = getLocalDraftKey(def);
    const clearSavedDraft = () => {
      setLogisticDraftById(prev => {
        const { [key]: _ignore, ...rest } = prev;
        return rest;
      });
      setLogisticDirtyById(prev => {
        const { [key]: _ignore, ...rest } = prev;
        return rest;
      });
    };
    if (def.type === 'contact_date') {
      const incoming = rawOverride !== undefined ? rawOverride : logisticDraftById[key];
      const obj = (incoming && typeof incoming === 'object') ? incoming : {};
      const contactId = String((obj as any)?.contactId || '').trim();
      const date = String((obj as any)?.date || '').trim().slice(0, 10);
      const toStore =
        (!contactId && !date)
          ? null
          : {
              contactId: contactId || null,
              date: date || null,
            };
      try {
        await saveLogisticValue(def, toStore);
        clearSavedDraft();
        setLogisticSaveErrorById(prev => {
          const { [key]: _ignore, ...rest } = prev;
          return rest;
        });
      } catch (e: any) {
        setLogisticSaveErrorById(prev => ({ ...prev, [key]: String(e?.message || 'Failed to save.') }));
      }
      return;
    }

    const raw = rawOverride !== undefined ? String(rawOverride) : String(logisticDraftById[key] ?? '');
    const trimmed = raw; // keep spaces for wifi passwords, etc.

    let toStore: any = trimmed;
    if (def.id === 'squad.priceCents') {
      const dollars = Number(trimmed);
      toStore = Number.isFinite(dollars) ? Math.round(dollars * 100) : null;
    } else if (def.id === 'squad.tuitionPerStudentPerClassCents' || def.id === 'squad.flatFeePerClassCents') {
      const dollars = Number(trimmed);
      toStore = Number.isFinite(dollars) ? Math.round(dollars * 100) : null;
    } else if (def.type === 'number') {
      toStore = trimmed === '' ? null : Number(trimmed);
      if (trimmed !== '' && !Number.isFinite(Number(toStore))) toStore = null;
    } else if (def.type === 'date' || def.type === 'time') {
      if (def.type === 'date') {
        toStore = trimmed === '' ? null : trimmed;
      } else {
        const parsed = parseFlexibleTimeTo24h(trimmed);
        if (!parsed.ok) {
          setLogisticSaveErrorById(prev => ({ ...prev, [key]: parsed.error }));
          return;
        }
        toStore = parsed.value === '' ? null : parsed.value;
      }
    } else if (def.type === 'enum') {
      toStore = trimmed === '' ? null : trimmed;
    } else if (def.type === 'boolean') {
      if (trimmed === '') toStore = null;
      else {
        const v = trimmed.trim().toLowerCase();
        toStore = v === 'true' || v === '1' || v === 'yes';
      }
    } else if (def.type === 'string_list' || def.type === 'file_list' || def.type === 'date_list') {
      const arr = trimmed
        .split('\n')
        .map(s => s.trim())
        .filter(Boolean);
      toStore = arr.length === 0 ? null : arr;
    } else {
      // text/url/etc
      toStore = trimmed === '' ? null : trimmed;
    }

    try {
      await saveLogisticValue(def, toStore);
      clearSavedDraft();
      setLogisticSaveErrorById(prev => {
        const { [key]: _ignore, ...rest } = prev;
        return rest;
      });
    } catch (e: any) {
      setLogisticSaveErrorById(prev => ({ ...prev, [key]: String(e?.message || 'Failed to save.') }));
    }
  };

  const uploadFileForLogistic = async (def: OpsLogisticDefinition, file: File, opts?: { appendToList?: boolean }) => {
    if (!selectedFlow) return;
    if (def.storage.kind === 'derived') return;

    const partnerId = String(selectedFlow.partnerId || '');
    const locationId = selectedLocationId;
    const squadId = String(selectedFlow.squadId || selectedFlow.squadIds?.[0] || '');
    const scopePrefix =
      def.storage.kind === 'locations'
        ? `locations/${locationId || 'unknown'}/documents`
        : def.storage.kind === 'partners' || def.storage.kind === 'partner_seasons'
          ? `partners/${partnerId || 'unknown'}/documents`
          : `squads/${squadId || 'unknown'}`;

    const rawPrefix = String(def.ui?.storagePathPrefix || `${scopePrefix}/opsUploads`);
    const prefix = rawPrefix
      .replace(/\{partnerId\}/g, partnerId || 'unknown')
      .replace(/\{locationId\}/g, locationId || 'unknown')
      .replace(/\{squadId\}/g, squadId || 'unknown')
      .replace(/^\/+/, '');
    const safeName = String(file.name || 'file').replace(/[^\w.\-]+/g, '_');
    const path = `${prefix}/${Date.now()}_${safeName}`;

    const key = getLocalDraftKey(def);
    try {
      // Mirror Firebase Storage rules to avoid confusing "unauthorized" errors.
      // Rules currently enforce size + contentType on some prefixes.
      const ref = storageRef(storage, path);
      const contentType = String((file as any)?.type || '').trim() || guessContentTypeFromFilename(file.name);
      await uploadBytes(ref, file, { contentType });
      const url = await getDownloadURL(ref);

      if (def.type === 'file_list' || opts?.appendToList) {
        const existing =
          def.storage.kind === 'partners'
            ? getValueAtPath(selectedPartner as any, def.storage.fieldPath)
            : def.storage.kind === 'locations'
              ? getValueAtPath(selectedLocation as any, def.storage.fieldPath)
              : def.storage.kind === 'partner_seasons'
                ? getValueAtPath(partnerSeason as any, def.storage.fieldPath)
                : getValueAtPath(selectedSquad as any, def.storage.fieldPath);
        const next = Array.isArray(existing) ? [...existing, url] : [url];
        await saveLogisticValue(def, next);
      } else {
        await saveLogisticValue(def, url);
      }
      setLogisticSaveErrorById(prev => {
        const { [key]: _ignore, ...rest } = prev;
        return rest;
      });
    } catch (e: any) {
      const msg = String(e?.message || 'Upload failed.');
      const hint =
        msg.toLowerCase().includes('storage/unauthorized')
          ? ' (Storage rules rejected this upload — usually file too large or wrong type, or your account is not recognized as admin by Storage rules.)'
          : '';
      setLogisticSaveErrorById(prev => ({ ...prev, [key]: `${msg}${hint}` }));
    }
  };

  const openBillingProfileEditor = async () => {
    setDerivedEditError(null);
    const partnerId = effectivePartnerId;
    if (!partnerId) return;
    try {
      const snap = await getDoc(doc(db, 'partners', partnerId));
      const raw = snap.exists() ? ((snap.data() as any)?.billingDefaults || {}) : {};
      const data = expandDottedTopLevelKeys(raw) || {};
      setBillingProfileDraft(data);
      const ids = Array.isArray((data as any)?.authorizedContactIds)
        ? ((data as any).authorizedContactIds as any[])
        : [];
      setBillingAuthorizedContactIds(ids.map(x => String(x || '').trim()).filter(Boolean));
      setBillingProfileDraftOpen(true);
    } catch (e: any) {
      setDerivedEditError(String(e?.message || 'Failed to load billing profile.'));
    }
  };

  const saveBillingProfile = async () => {
    setDerivedEditError(null);
    const partnerId = effectivePartnerId;
    if (!partnerId) return;
    try {
      await setDoc(doc(db, 'partners', partnerId), {
        billingDefaults: {
          ...(billingProfileDraft || {}),
          authorizedContactIds: billingAuthorizedContactIds,
        },
        updatedAt: new Date(),
        updatedBy: String((userData as any)?.id || ''),
      } as any, { merge: true });
      setBillingProfileDraftOpen(false);
      // refresh derived
      setDerivedOkByRuleId(prev => ({ ...prev, billingProfileExists: true }));
    } catch (e: any) {
      setDerivedEditError(String(e?.message || 'Failed to save billing profile.'));
    }
  };

  // (contact-based logistics handled via person-type UI; see derived logistics rendering)

  const materializeTemplateSnapshot = (t: OpsTemplate): OpsTemplate => {
    const checkpointsInline = Array.isArray((t as any).checkpoints) ? ((t as any).checkpoints as any[]) : null;
    if (checkpointsInline) return { ...(t as any), checkpoints: checkpointsInline } as OpsTemplate;

    const refs: OpsTemplateCheckpointRef[] = Array.isArray((t as any).checkpointRefs) ? ((t as any).checkpointRefs as any[]) : [];
    const missingCheckpointIds: string[] = [];
    const cps = refs
      .slice()
      .sort((a, b) => Number((a as any).order ?? 0) - Number((b as any).order ?? 0))
      .map((r) => {
        const id = safeString((r as any).checkpointId);
        const def = checkpointCatalog.byId.get(id);
        if (!def) {
          missingCheckpointIds.push(id || '(missing checkpoint ID)');
          return null;
        }
        return { ...(def as any), order: Number((r as any).order ?? 0) };
      })
      .filter(Boolean) as any[];

    if (missingCheckpointIds.length > 0) {
      throw new Error(
        `Template checkpoints are still loading or missing: ${Array.from(new Set(missingCheckpointIds)).join(', ')}. Try again; no template was applied.`,
      );
    }
    return { ...(t as any), checkpoints: cps } as OpsTemplate;
  };

  const effectiveTemplateUpdatedAt = (t: OpsTemplate | null | undefined): number => {
    if (!t) return 0;
    const base = Number((t as any)?.updatedAt || 0);
    let cpMax = 0;

    const inline = Array.isArray((t as any)?.checkpoints) ? (((t as any).checkpoints as any[]) || []) : null;
    if (inline) {
      for (const cp of inline) {
        const n = Number((cp as any)?.updatedAt || 0);
        if (Number.isFinite(n) && n > cpMax) cpMax = n;
      }
      return Math.max(base, cpMax);
    }

    const refs: OpsTemplateCheckpointRef[] = Array.isArray((t as any)?.checkpointRefs) ? ((t as any).checkpointRefs as any[]) : [];
    for (const r of refs) {
      const id = safeString((r as any)?.checkpointId);
      if (!id) continue;
      const def: any = checkpointCatalog.byId.get(id);
      const n = Number(def?.updatedAt || 0);
      if (Number.isFinite(n) && n > cpMax) cpMax = n;
    }
    return Math.max(base, cpMax);
  };

  const templateById = useMemo(() => {
    const m = new Map<string, OpsTemplate>();
    for (const t of templates || []) {
      const id = safeString((t as any)?.templateId);
      if (!id) continue;
      m.set(id, t);
    }
    return m;
  }, [templates]);

  const applySource = async (source: OpsAppliedSource) => {
    if (!selectedFlow) return;
    setApplyError(null);
    try {
      // Validate IDs (stable + unique). Prevents junk-drawer duplication.
      assertValidTemplateIds(source.snapshot);

      const next = applySourceToFlow({ flow: selectedFlow, source });
      const ref = doc(db, 'partnerSeasonFlows', selectedFlow.id);
      await updateDoc(ref, {
        appliedSources: next.appliedSources,
        checkpointState: next.checkpointState,
        taskState: next.taskState,
        updatedAt: Date.now(),
        updatedBy: (userData as any)?.id || '',
      } as any);
      setApplyModalOpen(false);
      setApplyQuery('');
    } catch (e: any) {
      setApplyError(String(e?.message || 'Failed to apply source.'));
    }
  };

  const updateAppliedTemplateAtIndex = async (idx: number) => {
    if (!selectedFlow) return;
    if (!Number.isFinite(idx) || idx < 0) return;
    setUpdateTemplateError(null);
    setUpdateTemplateErrorIdx(null);
    setUpdatingTemplateIdx(idx);
    try {
      const cur = Array.isArray((selectedFlow as any).appliedSources) ? (selectedFlow as any).appliedSources : [];
      const src = cur[idx] as any;
      if (!src) throw new Error('Template source not found.');
      const templateId = safeString(src?.sourceId);
      if (!templateId) throw new Error('Missing template id.');
      if (templateId === '__manual__') throw new Error('Manual flow checkpoints cannot be updated from a template.');

      // Always fetch the latest template doc before updating a snapshot.
      const snap = await getDoc(doc(db, 'opsTemplates', templateId));
      const latest = snap.exists()
        ? ({ ...(snap.data() as any), templateId } as OpsTemplate)
        : (templateById.get(templateId) as OpsTemplate | undefined);

      if (!latest) throw new Error(`Template not found: ${templateId}`);

      const snapshot = materializeTemplateSnapshot(latest);
      assertValidTemplateIds(snapshot);

      const nextSources = [...cur];
      nextSources[idx] = {
        ...(src as any),
        sourceType: 'template',
        sourceId: templateId,
        snapshot,
      } as any;

      // Ensure state keys exist for any newly added checkpoints/tasks.
      const withState = ensureFlowStateForSnapshot(
        { ...(selectedFlow as any), appliedSources: nextSources } as OpsFlow,
        snapshot
      );

      const ref = doc(db, 'partnerSeasonFlows', selectedFlow.id);
      await updateDoc(ref, {
        appliedSources: nextSources,
        checkpointState: withState.checkpointState,
        taskState: withState.taskState,
        updatedAt: Date.now(),
        updatedBy: (userData as any)?.id || '',
      } as any);
    } catch (e: any) {
      setUpdateTemplateError(String(e?.message || 'Failed to update template.'));
      setUpdateTemplateErrorIdx(idx);
    } finally {
      setUpdatingTemplateIdx(null);
    }
  };

  const addManualCheckpointToFlow = async () => {
    if (!selectedFlow) return;
    setApplyError(null);

    const pickId = safeString(addCheckpointPickId);
    if (!pickId) return;
    const existingState = (selectedFlow.checkpointState || {})[pickId];
    const existsInSnapshots = allCheckpointIdsFromSnapshots.has(pickId);
    const isSuperseded = existingState?.status === 'superseded';

    // If it already exists (even if hidden), prefer "revive" over creating duplicates.
    if (existsInSnapshots || allCheckpointIds.has(pickId)) {
      if (isSuperseded) {
        try {
          const ref = doc(db, 'partnerSeasonFlows', selectedFlow.id);
          const now = Date.now();
          const actor = String((userData as any)?.id || '');
          await updateDoc(ref, {
            checkpointState: {
              ...(selectedFlow.checkpointState || {}),
              [pickId]: { ...(existingState || {}), status: 'not_started' },
            },
            updatedAt: now,
            updatedBy: actor,
          } as any);
          setAddCheckpointPickId('');
          setAddCheckpointOpen(false);
          return;
        } catch (e: any) {
          setApplyError(String(e?.message || 'Failed to restore checkpoint.'));
          return;
        }
      }
      setApplyError(`Checkpoint already exists in this flow: ${pickId}`);
      return;
    }

    const base = checkpointCatalog.byId.get(pickId);
    if (!base) {
      setApplyError('Checkpoint not found in catalog.');
      return;
    }

    // Default order: append to the end of the overall list (can be reordered later).
    const order = allCheckpointIds.size;

    // Clone checkpoint definition, but append-order it for this flow.
    const checkpoint = {
      ...(base as any),
      checkpointId: pickId,
      order,
    };

    const curSources = Array.isArray((selectedFlow as any).appliedSources) ? (selectedFlow as any).appliedSources : [];
    const manualSourceId = '__manual__';
    const idx = curSources.findIndex((s: any) => safeString(s?.sourceId) === manualSourceId);

    const now = Date.now();
    const actor = String((userData as any)?.id || '');

    const manualSnapshot: OpsTemplate =
      idx >= 0 && curSources[idx]?.snapshot
        ? (curSources[idx].snapshot as OpsTemplate)
        : ({
            templateId: manualSourceId,
            title: 'Manual',
            description: 'Flow-local checkpoints added by ops.',
            checkpoints: [],
            createdAt: now,
            createdBy: actor,
            updatedAt: now,
            updatedBy: actor,
          } as OpsTemplate);

    const nextManualSnapshot: OpsTemplate = {
      ...(manualSnapshot as any),
      checkpoints: [...(manualSnapshot.checkpoints || []), checkpoint as any],
      updatedAt: now,
      updatedBy: actor,
    };

    const nextSources = [...curSources];
    if (idx >= 0) {
      nextSources[idx] = { ...(nextSources[idx] as any), sourceType: 'template', sourceId: manualSourceId, snapshot: nextManualSnapshot } as any;
    } else {
      nextSources.push({ sourceType: 'template', sourceId: manualSourceId, snapshot: nextManualSnapshot } as any);
    }

    // Ensure flow state entries exist for the new checkpoint.
    const withState = ensureFlowStateForSnapshot(
      { ...(selectedFlow as any), appliedSources: nextSources } as OpsFlow,
      { checkpoints: [checkpoint as any] } as any
    );

    try {
      const ref = doc(db, 'partnerSeasonFlows', selectedFlow.id);
      await updateDoc(ref, {
        appliedSources: nextSources,
        checkpointState: withState.checkpointState,
        taskState: withState.taskState,
        updatedAt: now,
        updatedBy: actor,
      } as any);
      setAddCheckpointPickId('');
      setAddCheckpointOpen(false);
    } catch (e: any) {
      setApplyError(String(e?.message || 'Failed to add checkpoint.'));
    }
  };

  const reorderCheckpointsInSection = async (params: { sectionKey: string; orderedCheckpointIds: string[] }) => {
    const { sectionKey, orderedCheckpointIds } = params;
    if (!selectedFlow) return;
    if (!sectionKey.startsWith('source:')) return;
    if (!Array.isArray(orderedCheckpointIds) || orderedCheckpointIds.length === 0) return;

    const now = Date.now();
    const actor = String((userData as any)?.id || '');
    const ref = doc(db, 'partnerSeasonFlows', selectedFlow.id);
    const batch = writeBatch(db);

    orderedCheckpointIds.forEach((checkpointId, idx) => {
      batch.update(ref, new FieldPath('checkpointState', checkpointId, 'orderOverride') as any, idx);
    });
    batch.update(ref, { updatedAt: now, updatedBy: actor } as any);

    try {
      await batch.commit();
    } catch (e: any) {
      setApplyError(String(e?.message || 'Failed to reorder checkpoints.'));
    }
  };

  const removeAppliedTemplateAtIndex = async (idx: number) => {
    if (!selectedFlow) return;
    if (!Number.isFinite(idx) || idx < 0) return;
    const cur = Array.isArray((selectedFlow as any).appliedSources) ? (selectedFlow as any).appliedSources : [];
    const src = cur[idx] as any;
    if (!src) return;

    const title = safeString(src?.snapshot?.title || src?.sourceId || `template #${idx + 1}`);
    const ok = await window.appConfirm(`Remove applied template from this flow?\n\n${title}\n\nThis hides its checkpoints without deleting partner, location, or squad data.`);
    if (!ok) return;

    try {
      const nextApplied = cur.filter((_: any, i: number) => i !== idx);
      const ref = doc(db, 'partnerSeasonFlows', selectedFlow.id);
      await updateDoc(ref, {
        appliedSources: nextApplied,
        updatedAt: Date.now(),
        updatedBy: (userData as any)?.id || '',
      } as any);
    } catch (e: any) {
      setApplyError(String(e?.message || 'Failed to remove template.'));
    }
  };

  if (!isAdmin) {
    return (
      <div style={{ padding: 16 }}>
        <h2 style={{ marginTop: 0 }}>Flows</h2>
        <div className="muted">Admin access required.</div>
      </div>
    );
  }

  return (
    <div style={props.hideList ? { padding: 16 } : { display: 'grid', gridTemplateColumns: '360px 1fr', height: 'calc(100vh - 84px)' }}>
      {/* Left list */}
      {!props.hideList ? (
      <div style={{ borderRight: '1px solid #e5e7eb', padding: 12, overflow: 'auto' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
          <input
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
            placeholder="Search partners, locations, or squads…"
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8 }}
          />
        </div>
        {isEditMode ? (
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, fontSize: 13, color: '#374151' }}>
            <input type="checkbox" checked={hideSuperseded} onChange={(e) => setHideSuperseded(e.target.checked)} />
            Hide superseded
          </label>
        ) : null}

        {rows.length === 0 ? (
          <div className="muted" style={{ padding: 8 }}>No flows found.</div>
        ) : (
          rows.map((r) => {
            const isActive = r.id === selectedFlowId;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setSelectedFlowId(r.id)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  border: '1px solid #e5e7eb',
                  borderRadius: 10,
                  padding: 10,
                  marginBottom: 8,
                  background: isActive ? '#f3f4f6' : 'white',
                }}
              >
                <div style={{ fontWeight: 700, color: '#111827' }}>{r.squadTitle}</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                  {r.partnerName}{r.locationNames.length ? ` • ${r.locationNames.join(', ')}` : ''}
                </div>
              </button>
            );
          })
        )}
      </div>
      ) : null}

      {/* Right detail */}
      <div style={props.hideList ? { padding: 12, overflow: 'auto' } : { padding: 16, overflow: 'auto' }}>
        {!selectedFlow ? (
          <div className="muted">Flow not found.</div>
        ) : (
          <>
            <div
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 50,
                background: 'rgba(255,255,255,0.96)',
                backdropFilter: 'blur(10px)',
                borderBottom: '1px solid #eef2f7',
                paddingBottom: 10,
                marginBottom: 10,
              }}
            >
              {props.showBackToList ? (
                <button
                  type="button"
                  className="mini-btn"
                  onClick={() => navigate('/app/admin/ops/flows')}
                  style={{ marginBottom: 10 }}
                >
                  ← Back to flows
                </button>
              ) : null}

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                <div>
                  {editingSquadTitle ? (
                    <input
                      type="text"
                      value={squadTitleDraft}
                      onChange={(e) => setSquadTitleDraft(e.target.value)}
                      onBlur={() => void saveSquadTitle(squadTitleDraft)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void saveSquadTitle(squadTitleDraft);
                        if (e.key === 'Escape') {
                          setEditingSquadTitle(false);
                          setSquadTitleError(null);
                          setSquadTitleDraft('');
                        }
                      }}
                      autoFocus
                      disabled={savingSquadTitle}
                      style={{
                        fontSize: 24,
                        fontWeight: 700,
                        padding: '6px 10px',
                        borderRadius: 10,
                        border: '1px solid #e5e7eb',
                        minWidth: 260,
                      }}
                    />
                  ) : (
                    <h2
                      style={{ marginTop: 0, marginBottom: 6, cursor: 'pointer' }}
                      title="Click to rename squad"
                      onClick={() => {
                        if (!isAdmin) return;
                        setEditingSquadTitle(true);
                        setSquadTitleError(null);
                        setSquadTitleDraft(String((selectedSquad as any)?.title || selectedFlow.squadId));
                      }}
                    >
                      {String((selectedSquad as any)?.title || selectedFlow.squadId)}
                    </h2>
                  )}
                  {squadTitleError ? (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#991b1b' }}>
                      {squadTitleError}
                    </div>
                  ) : null}

                  {/* Status row: stage + owner grouped together */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div
                      title={`Stage: ${pipelineStageLabel(resolvedPipelineStage)}`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '3px 8px 3px 10px',
                        borderRadius: 999,
                        background: (OPS_STAGE_COLORS[resolvedPipelineStage] || OPS_STAGE_COLORS.uncategorized).bg,
                        border: `1px solid ${(OPS_STAGE_COLORS[resolvedPipelineStage] || OPS_STAGE_COLORS.uncategorized).border}`,
                        color: (OPS_STAGE_COLORS[resolvedPipelineStage] || OPS_STAGE_COLORS.uncategorized).text,
                        opacity: savingStage ? 0.6 : 1,
                      }}
                    >
                      <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4 }}>Stage</span>
                      <select
                        value={resolvedPipelineStage}
                        disabled={!isAdmin || savingStage}
                        onChange={(e) => void savePipelineStage(e.target.value as OpsPipelineStageId, resolvedPipelineStage)}
                        aria-label="Change pipeline stage"
                        style={{
                          border: 0,
                          background: 'transparent',
                          color: 'inherit',
                          fontWeight: 800,
                          fontSize: 13,
                          cursor: isAdmin ? 'pointer' : 'default',
                          outline: 'none',
                        }}
                      >
                        {OPS_PIPELINE_STAGES.map((s) => (
                          <option key={s.id} value={s.id} style={{ color: '#111827' }}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                    <OwnerDropdown
                      ownerId={String((selectedFlow as any).internalOwnerUserId || '').trim()}
                      adminUsers={adminUsers}
                      adminById={adminById}
                      disabled={!isAdmin}
                      onSelect={(id) => void saveInternalOwner(id)}
                    />
                  </div>
                </div>
                <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      className={isEditMode ? 'btn btn-secondary' : 'btn btn-primary'}
                      onClick={() => {
                        setIsEditMode(v => !v);
                        setApplyModalOpen(false);
                        setApplyError(null);
                        setApplyQuery('');
                      }}
                    >
                      {isEditMode ? 'Done' : 'Edit'}
                    </button>
                    <div ref={actionsMenuRef} style={{ position: 'relative' }}>
                      <button
                        type="button"
                        className="mini-btn"
                        onClick={() => setActionsMenuOpen((v) => !v)}
                        aria-haspopup="menu"
                        aria-expanded={actionsMenuOpen}
                        title="More actions"
                        style={{ fontSize: 18, lineHeight: 1, padding: '4px 10px' }}
                      >
                        ⋯
                      </button>
                      {actionsMenuOpen ? (
                        <div
                          role="menu"
                          style={{
                            position: 'absolute',
                            top: 'calc(100% + 6px)',
                            right: 0,
                            zIndex: 60,
                            minWidth: 180,
                            background: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: 12,
                            boxShadow: '0 18px 40px rgba(0,0,0,0.16)',
                            padding: 6,
                            display: 'grid',
                            gap: 2,
                            textAlign: 'left',
                          }}
                        >
                          <button
                            type="button"
                            role="menuitem"
                            className="mini-btn"
                            onClick={() => {
                              setActionsMenuOpen(false);
                              const sid = safeString(selectedFlow?.squadId);
                              if (!sid) return;
                              navigate(`/app/admin/squads/${sid}`);
                            }}
                            disabled={!safeString(selectedFlow?.squadId)}
                            style={{ width: '100%', justifyContent: 'flex-start', border: 0, background: 'transparent', textAlign: 'left' }}
                          >
                            Squad details
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            className="mini-btn"
                            onClick={() => {
                              setActionsMenuOpen(false);
                              void deleteCurrentFlow();
                            }}
                            disabled={!isAdmin || deletingFlow}
                            style={{ width: '100%', justifyContent: 'flex-start', border: 0, background: 'transparent', color: '#b91c1c', textAlign: 'left' }}
                          >
                            {deletingFlow ? 'Deleting…' : 'Delete flow'}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  {deleteFlowError ? (
                    <div style={{ marginTop: 2, fontSize: 12, color: '#991b1b', maxWidth: 420, textAlign: 'right' }}>
                      {deleteFlowError}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {outreachPanelOpen ? (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, marginTop: 12, marginBottom: 12, background: '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                  <div style={{ fontWeight: 900 }}>Log outreach</div>
                  <button type="button" className="mini-btn" onClick={() => setOutreachPanelOpen(false)}>Close</button>
                </div>

                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 800 }}>Step 1 — Channel</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                    {([
                      { id: 'email' as const, label: 'Email' },
                      { id: 'call' as const, label: 'Call' },
                      { id: 'in_person' as const, label: 'In person' },
                      { id: 'other' as const, label: 'Other' },
                    ] as Array<{ id: OpsOutreachChannel; label: string }>).map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        className={outreachChannel === o.id ? 'btn btn-primary' : 'btn btn-secondary'}
                        onClick={() => setOutreachChannel(o.id)}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 800 }}>Step 2 — What was this about?</div>
                  <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
                    Default: all missing logistics in the current checkpoint.
                  </div>

                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, fontSize: 13, color: '#374151' }}>
                    <input
                      type="checkbox"
                      checked={outreachIncludeOtherMissing}
                      onChange={(e) => setOutreachIncludeOtherMissing(e.target.checked)}
                    />
                    Show other missing logistics in this flow
                  </label>

                  <div style={{ marginTop: 10, border: '1px solid #e5e7eb', borderRadius: 10, padding: 10, background: '#f9fafb', maxHeight: 240, overflow: 'auto' }}>
                    {(() => {
                      const selected = new Set(outreachSelectedLogisticIds.map(String));
                      const base = missingLogisticsInCurrentCheckpoint;
                      const baseIds = new Set(base.map(x => x.logisticId));
                      const extras = outreachIncludeOtherMissing
                        ? missingLogisticsInFlow.filter(x => !baseIds.has(x.logisticId))
                        : [];
                      const rows = [...base, ...extras];
                      if (rows.length === 0) return <div className="muted" style={{ fontSize: 12 }}>No missing logistics found.</div>;
                      return (
                        <div style={{ display: 'grid', gap: 6 }}>
                          {rows.map((x) => (
                            <label key={x.logisticId} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                              <input
                                type="checkbox"
                                checked={selected.has(x.logisticId)}
                                onChange={() => {
                                  setOutreachSelectedLogisticIds((prev) => {
                                    const s = new Set(prev.map(String));
                                    if (s.has(x.logisticId)) s.delete(x.logisticId);
                                    else s.add(x.logisticId);
                                    return Array.from(s);
                                  });
                                }}
                                style={{ marginTop: 2 }}
                              />
                              <div style={{ fontSize: 13, color: '#111827' }}>{x.title}</div>
                            </label>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 800 }}>Step 3 — Apply</div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
                    <button type="button" className="btn btn-secondary" onClick={() => setOutreachPanelOpen(false)}>Cancel</button>
                    <button type="button" className="btn btn-primary" onClick={() => void applyOutreach()}>
                      Done
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            <div
              style={{
                marginTop: 12,
                marginBottom: 12,
                padding: 12,
                border: '1px solid #e5e7eb',
                borderRadius: 12,
                background: 'white',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    openFlowTodoEditor(e.currentTarget as any);
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    openFlowTodoEditor(e.currentTarget as any);
                  }}
                  title="View/edit TODO for this flow"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    cursor: 'pointer',
                    whiteSpace: 'normal',
                    flex: 1,
                    minWidth: 320,
                    maxWidth: 720,
                  }}
                >
                  <div style={{ fontWeight: 900, color: '#111827', fontSize: 16, letterSpacing: 0.2 }}>
                    TODO:
                  </div>
                  <div
                    style={{
                      flex: 1,
                      fontSize: 15,
                      color: '#111827',
                      fontWeight: 700,
                      lineHeight: 1.3,
                      border: '1px solid #e5e7eb',
                      background: 'white',
                      borderRadius: 12,
                      padding: '10px 12px',
                      display: '-webkit-box',
                      WebkitLineClamp: 2 as any,
                      WebkitBoxOrient: 'vertical' as any,
                      overflow: 'hidden',
                    }}
                  >
                    {(() => {
                      const preview = normalizeTodoText(effectiveFlowTodo.note || '');
                      return preview || 'Tap to add a TODO for this flow.';
                    })()}
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr',
                    columnGap: 16,
                    rowGap: 7,
                    alignItems: 'center',
                    maxWidth: 520,
                  }}
                >
                  {(() => {
                    const ymd = String(effectiveFlowTodo.dueDateYmd || '').trim();
                    const label = ymd ? (dueLabelFromYmd(ymd)?.text || 'No due date') : 'No due date';
                    const overdue = /overdue/i.test(label);
                    return (
                      <>
                        <div style={{ fontSize: 12, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>Due</div>
                        <div style={{ fontWeight: 800, color: overdue ? '#b91c1c' : '#111827' }}>
                          {label}{ymd ? ` · ${formatMmDdFromYmd(effectiveFlowTodo.dueDateYmd)}` : ''}
                        </div>
                      </>
                    );
                  })()}

                  <div style={{ fontSize: 12, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>Priority</div>
                  <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
                    {editingPriority ? (
                      <input
                        ref={priorityInputRef}
                        type="number"
                        min={0}
                        max={10}
                        step={1}
                        value={riskDraft}
                        onChange={(e) => setRiskDraft(e.target.value)}
                        onBlur={() => {
                          setEditingPriority(false);
                          void saveRiskScore(riskDraft);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
                          if (e.key === 'Escape') {
                            const v = (selectedFlow as any)?.riskScoreOutOf10;
                            setRiskDraft(v === null || v === undefined ? '' : String(v));
                            (e.currentTarget as HTMLInputElement).blur();
                          }
                        }}
                        placeholder="—"
                        inputMode="numeric"
                        style={{
                          width: 48,
                          padding: '2px 4px',
                          border: '0',
                          borderBottom: '2px solid #111827',
                          borderRadius: 0,
                          outline: 'none',
                          background: 'transparent',
                          fontSize: 18,
                          fontWeight: 900,
                          lineHeight: 1,
                          color: '#111827',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                        title="Manual priority score (0–10)"
                        aria-label="Priority (0–10)"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setEditingPriority(true)}
                        title="Click to edit priority (0–10)"
                        aria-label="Edit priority (0–10)"
                        style={{
                          border: 0,
                          background: 'transparent',
                          padding: 0,
                          cursor: 'pointer',
                          fontSize: 18,
                          fontWeight: 900,
                          lineHeight: 1,
                          color: '#111827',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {String(riskDraft || '—')}
                      </button>
                    )}
                    <span style={{ fontWeight: 700, color: '#6b7280', fontSize: 13 }}>/ 10</span>
                  </div>
                </div>

              </div>
            </div>

            {copyToast ? (
              <div
                style={{
                  position: 'fixed',
                  right: 16,
                  bottom: 16,
                  zIndex: 9999,
                  background: '#111827',
                  color: 'white',
                  padding: '10px 12px',
                  borderRadius: 10,
                  fontSize: 12,
                  fontWeight: 800,
                  boxShadow: '0 12px 30px rgba(0,0,0,0.25)',
                }}
                role="status"
                aria-live="polite"
              >
                {copyToast}
              </div>
            ) : null}

            {todoEditor ? (
              (() => {
                const margin = 12;
                const winW = typeof window !== 'undefined' ? window.innerWidth : 1200;
                const winH = typeof window !== 'undefined' ? window.innerHeight : 800;
                const width = todoEditor.isMobile ? Math.min(520, winW - margin * 2) : 420;
                const estHeight = todoEditor.isMobile ? Math.min(560, winH - margin * 2) : 280;
                const rect = todoEditor.anchorRect;
                let left = todoEditor.isMobile ? margin : Math.min(Math.max(margin, rect.left), winW - width - margin);
                let top = todoEditor.isMobile ? margin : (rect.bottom + 8);
                if (!todoEditor.isMobile && top + estHeight + margin > winH) top = Math.max(margin, rect.top - estHeight - 8);

                const normalized = normalizeTodoText(todoEditor.draft);
                const dueDraftYmd = String(todoEditor.dueDateDraftYmd || '').slice(0, 10);
                const dirty = normalized !== todoEditor.initialNormalized || dueDraftYmd !== String(todoEditor.initialDueDateYmd || '').slice(0, 10);
                const canClear = Boolean(todoEditor.initialNormalized) || Boolean(String(todoEditor.initialDueDateYmd || '').slice(0, 10));
                const count = String(todoEditor.draft || '').length;
                const dueRequiredMissing = !dueDraftYmd;

                return (
                  <div
                    style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(17,24,39,0.08)' }}
                    onPointerDown={closeTodoEditor}
                    onMouseDown={closeTodoEditor}
                  >
                    <div
                      role="dialog"
                      aria-label={`TODO editor for ${todoEditor.checkpointTitle}`}
                      onPointerDown={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{
                        position: 'fixed',
                        left,
                        top,
                        width,
                        maxWidth: `calc(100vw - ${margin * 2}px)`,
                        border: '1px solid #e5e7eb',
                        borderRadius: 12,
                        background: 'white',
                        boxShadow: '0 18px 40px rgba(0,0,0,0.18)',
                        padding: 12,
                        maxHeight: todoEditor.isMobile ? `calc(100vh - ${margin * 2}px)` : undefined,
                        overflow: todoEditor.isMobile ? 'auto' : undefined,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                        <div>
                          <div style={{ fontWeight: 900, color: '#111827' }}>
                            {todoEditor.target === 'flow' ? 'TODO' : `TODO for ${todoEditor.checkpointTitle}`}
                          </div>
                          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                            {todoEditor.target === 'flow'
                              ? 'Next step notes for this flow (visible to ops).'
                              : 'Next step notes for this checkpoint (visible to ops).'}
                          </div>
                        </div>
                        <button type="button" className="mini-btn" onClick={closeTodoEditor}>
                          Close
                        </button>
                      </div>

                      {todoError ? (
                        <div className="billing-error" style={{ marginTop: 10 }}>
                          {todoError}
                        </div>
                      ) : null}

                      <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'end', gap: 10 }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: 12, color: '#6b7280', fontWeight: 800 }}>Due date (required)</label>
                          <input
                            type="date"
                            value={dueDraftYmd}
                            onChange={(e) => {
                              const next = String(e.target.value || '').slice(0, 10);
                              setTodoEditor(prev => prev ? ({ ...prev, dueDateDraftYmd: next }) : prev);
                              setTodoError(null);
                              if (todoEditor.target === 'checkpoint') {
                                void updateCheckpoint(todoEditor.checkpointId, { dueDate: next || null });
                              }
                            }}
                            style={{ width: 190, marginTop: 4 }}
                          />
                        </div>
                        {dueRequiredMissing ? (
                          <div
                            style={{
                              fontSize: 12,
                              color: '#b45309',
                              background: '#fffbeb',
                              border: '1px solid #f59e0b',
                              borderRadius: 10,
                              padding: '8px 10px',
                              fontWeight: 800,
                            }}
                          >
                            Enter a due date to save this TODO.
                          </div>
                        ) : null}
                      </div>

                      <div style={{ marginTop: 10 }}>
                        <textarea
                          ref={todoTextareaRef}
                          value={todoEditor.draft}
                          onChange={(e) => {
                            const v = String(e.target.value || '').slice(0, TODO_MAX_LEN);
                            setTodoEditor(prev => prev ? ({ ...prev, draft: v }) : prev);
                            setTodoError(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                              e.preventDefault();
                              void saveTodoEditor();
                            }
                          }}
                          placeholder="What’s the next step?"
                          maxLength={TODO_MAX_LEN}
                          rows={4}
                          style={{
                            width: '100%',
                            padding: 10,
                            border: '1px solid #e5e7eb',
                            borderRadius: 10,
                            fontSize: 13,
                            lineHeight: 1.4,
                            resize: 'vertical',
                          }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, gap: 10 }}>
                          <div className="muted" style={{ fontSize: 12 }}>
                            Tip: Cmd/Ctrl+Enter to save · Esc to close
                          </div>
                          <div className="muted" style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                            {count}/{TODO_MAX_LEN}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginTop: 12 }}>
                        <div>
                          {canClear ? (
                            <button
                              type="button"
                              className="mini-btn danger"
                              onClick={() => void saveTodoEditor({ clear: true })}
                              disabled={todoSaving}
                              title="Clear this TODO"
                            >
                              Clear
                            </button>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => void saveTodoEditor()}
                          disabled={!dirty || todoSaving || dueRequiredMissing}
                          title={
                            dueRequiredMissing
                              ? 'Enter a due date to save'
                              : (!dirty ? 'No changes to save' : undefined)
                          }
                        >
                          {todoSaving ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()
            ) : null}


            {applyModalOpen && (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                  <div style={{ fontWeight: 800 }}>
                    Add template
                  </div>
                  <button type="button" className="mini-btn" onClick={() => setApplyModalOpen(false)}>Close</button>
                </div>

                <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
                  <input
                    value={applyQuery}
                    onChange={(e) => setApplyQuery(e.target.value)}
                    placeholder="Search…"
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8 }}
                    autoFocus
                  />
                </div>

                {applyError ? <div className="billing-error" style={{ marginTop: 10 }}>{applyError}</div> : null}

                <div style={{ marginTop: 10, maxHeight: 220, overflow: 'auto' }}>
                  {(templates as any[])
                    .filter((x: any) => {
                      const needle = safeString(applyQuery).toLowerCase();
                      if (!needle) return true;
                      const label = safeString(x.title || x.templateId).toLowerCase();
                      const id = safeString(x.templateId).toLowerCase();
                      return label.includes(needle) || id.includes(needle);
                    })
                    .slice(0, 50)
                    .map((x: any) => {
                      const id = String(x.templateId);
                      const title = safeString(x.title || id);
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => {
                            void (async () => {
                              try {
                                // Always fetch the latest template doc before applying,
                                // otherwise you can apply a stale in-memory snapshot when switching tabs quickly.
                                const snap = await getDoc(doc(db, 'opsTemplates', id));
                                const latest = snap.exists()
                                  ? ({ ...(snap.data() as any), templateId: id } as OpsTemplate)
                                  : (x as OpsTemplate);
                                const snapshot = materializeTemplateSnapshot(latest);
                                await applySource({ sourceType: 'template', sourceId: id, snapshot });
                              } catch (e: any) {
                                setApplyError(String(e?.message || 'Failed to apply template.'));
                              }
                            })();
                          }}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            border: '1px solid #e5e7eb',
                            borderRadius: 10,
                            padding: 10,
                            marginBottom: 8,
                            background: 'white',
                          }}
                        >
                          <div style={{ fontWeight: 700, color: '#111827' }}>{title}</div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{id}</div>
                        </button>
                      );
                    })}
                  {templates.length === 0 ? (
                    <div className="muted" style={{ padding: 8 }}>
                      No templates yet. Create one in the Templates tab.
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {/* Edit-mode quick add controls */}
            {isEditMode ? (
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => { setApplyModalOpen(true); setApplyQuery(''); setApplyError(null); }}
                    style={{
                      width: 110,
                      height: 76,
                      borderRadius: 12,
                      border: '1px solid #e5e7eb',
                      background: 'white',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                    }}
                  >
                    <div style={{ fontSize: 24, fontWeight: 900, lineHeight: 1 }}>+</div>
                    <div style={{ fontSize: 12, color: '#374151', fontWeight: 700 }}>Add template</div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setAddCheckpointOpen(v => !v)}
                    style={{
                      width: 110,
                      height: 76,
                      borderRadius: 12,
                      border: '1px solid #e5e7eb',
                      background: 'white',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                    }}
                  >
                    <div style={{ fontSize: 24, fontWeight: 900, lineHeight: 1 }}>+</div>
                    <div style={{ fontSize: 12, color: '#374151', fontWeight: 700 }}>Add checkpoint</div>
                  </button>
                </div>

                {addCheckpointOpen ? (
                  <div style={{ marginTop: 12, border: '1px solid #e5e7eb', borderRadius: 12, padding: 12 }}>
                    <div style={{ fontWeight: 800, marginBottom: 8 }}>Add checkpoint</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 10, alignItems: 'center' }}>
                      <SearchableSelect
                        valueId={addCheckpointPickId}
                        placeholder="Search checkpoints…"
                        options={checkpointCatalog.options}
                        onSelectId={(id) => setAddCheckpointPickId(id)}
                      />
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => void addManualCheckpointToFlow()}
                        disabled={!safeString(addCheckpointPickId)}
                      >
                        Add
                      </button>
                    </div>
                    {applyError ? <div className="billing-error" style={{ marginTop: 10 }}>{applyError}</div> : null}
                    <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                      Checkpoints come from existing templates.
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {sections.map((s) => {
              const sourceIdx = s.key.startsWith('source:') ? Number(s.key.split(':')[1]) : NaN;
              const appliedSrc =
                selectedFlow && Number.isFinite(sourceIdx) && sourceIdx >= 0
                  ? (((selectedFlow as any).appliedSources || [])[sourceIdx] as any)
                  : null;
              const appliedTemplateId = safeString(appliedSrc?.sourceId);
              const latestTemplate = appliedTemplateId ? (templateById.get(appliedTemplateId) || null) : null;
              const snapshotUpdatedAt = effectiveTemplateUpdatedAt(appliedSrc?.snapshot || null);
              const latestUpdatedAt = effectiveTemplateUpdatedAt(latestTemplate);
              const canUpdateTemplate =
                Boolean(appliedSrc)
                && safeString(appliedSrc?.sourceType) === 'template'
                && Boolean(appliedTemplateId)
                && appliedTemplateId !== '__manual__'
                && Number.isFinite(latestUpdatedAt)
                && latestUpdatedAt > snapshotUpdatedAt;
              return (
                <div key={s.key} style={{ border: '1px solid #e5e7eb', borderRadius: 12, marginBottom: 12 }}>
                  <div style={{ padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontWeight: 800 }}>
                      {s.title}
                      {s.sourceId ? (
                        <span style={{ marginLeft: 8, fontSize: 12, color: '#6b7280' }}>{s.sourceType}: {s.sourceId}</span>
                      ) : null}
                    </div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      {canUpdateTemplate ? (
                        <button
                          type="button"
                          className="mini-btn"
                          onClick={() => void updateAppliedTemplateAtIndex(sourceIdx)}
                          disabled={updatingTemplateIdx === sourceIdx}
                          title="Update this flow’s applied template snapshot to the latest version"
                        >
                          {updatingTemplateIdx === sourceIdx ? 'Updating…' : 'Update template'}
                        </button>
                      ) : null}
                      {isEditMode ? (
                        <button
                          type="button"
                          className="mini-btn danger"
                          onClick={() => void removeAppliedTemplateAtIndex(sourceIdx)}
                          title="Remove this applied template from the flow"
                        >
                          Remove template
                        </button>
                      ) : null}
                    </div>
                  </div>

                  <div style={{ padding: 12, paddingTop: 0 }}>
                      {updateTemplateError && updateTemplateErrorIdx === sourceIdx ? (
                        <div className="billing-error" style={{ marginBottom: 10 }}>{updateTemplateError}</div>
                      ) : null}
                      {s.checkpoints.length === 0 ? (
                        <div className="muted" style={{ padding: 6 }}>No checkpoints.</div>
                      ) : (
                        s.checkpoints.map(({ checkpoint, tasks }) => {
                          const cpState = (selectedFlow.checkpointState || {})[checkpoint.checkpointId] || { status: 'not_started' };
                          const prog = checkpointProgressById[String(checkpoint.checkpointId)] || { percent: 0, ok: 0, total: 0 };
                          const gate = checkpointGateStatusById[String(checkpoint.checkpointId)] || { state: 'not_started' as const, percent: prog.percent };
                          const logisticCount = prog.total;
                          const isCheckpointCollapsed = Boolean(collapsedCheckpointIds[checkpoint.checkpointId]);
                          const isDragOver = isEditMode && dragOverCheckpointId === checkpoint.checkpointId && draggingSectionKey === s.key;
                          return (
                            <div
                              key={checkpoint.checkpointId}
                              style={{
                                borderTop: '1px solid #f3f4f6',
                                paddingTop: 10,
                                marginTop: 10,
                                outline: isDragOver ? '2px solid #60a5fa' : 'none',
                                outlineOffset: 2,
                                borderRadius: 8,
                              }}
                              onDragOver={(e) => {
                                if (!isEditMode) return;
                                if (!draggingCheckpointId) return;
                                if (draggingSectionKey !== s.key) return;
                                e.preventDefault();
                                setDragOverCheckpointId(checkpoint.checkpointId);
                              }}
                              onDrop={(e) => {
                                if (!isEditMode) return;
                                e.preventDefault();
                                const draggedId = draggingCheckpointId || e.dataTransfer.getData('text/plain');
                                if (!draggedId) return;
                                if (draggingSectionKey !== s.key) return; // keep it simple: reorder within a section for now
                                if (draggedId === checkpoint.checkpointId) return;

                                const ids = s.checkpoints.map((row) => String(row.checkpoint.checkpointId || '').trim()).filter(Boolean);
                                const from = ids.indexOf(draggedId);
                                const to = ids.indexOf(checkpoint.checkpointId);
                                if (from < 0 || to < 0) return;

                                const next = [...ids];
                                next.splice(from, 1);
                                next.splice(to, 0, draggedId);
                                void reorderCheckpointsInSection({ sectionKey: s.key, orderedCheckpointIds: next });
                                setDraggingCheckpointId(null);
                                setDraggingSectionKey(null);
                                setDragOverCheckpointId(null);
                              }}
                              onDragLeave={() => {
                                if (dragOverCheckpointId === checkpoint.checkpointId) setDragOverCheckpointId(null);
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  {isEditMode ? (
                                    <button
                                      type="button"
                                      className="mini-btn"
                                      draggable
                                      onDragStart={(e) => {
                                        setDraggingCheckpointId(checkpoint.checkpointId);
                                        setDraggingSectionKey(s.key);
                                        setDragOverCheckpointId(null);
                                        try {
                                          e.dataTransfer.setData('text/plain', checkpoint.checkpointId);
                                          e.dataTransfer.effectAllowed = 'move';
                                        } catch {
                                          // ignore
                                        }
                                      }}
                                      onDragEnd={() => {
                                        setDraggingCheckpointId(null);
                                        setDraggingSectionKey(null);
                                        setDragOverCheckpointId(null);
                                      }}
                                      title="Drag to reorder (within this section)"
                                      style={{ cursor: 'grab', width: 34, textAlign: 'center' }}
                                    >
                                      ⋮⋮
                                    </button>
                                  ) : null}
                                  <button
                                    type="button"
                                    className="mini-btn"
                                    onClick={() => setCollapsedCheckpointIds(prev => ({ ...prev, [checkpoint.checkpointId]: !prev[checkpoint.checkpointId] }))}
                                    title={isCheckpointCollapsed ? 'Expand checkpoint' : 'Collapse checkpoint'}
                                    style={{ width: 40, height: 34, textAlign: 'center', fontSize: 18, fontWeight: 900, lineHeight: 1 }}
                                  >
                                    {isCheckpointCollapsed ? '▸' : '▾'}
                                  </button>
                                  <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                      <div
                                        style={{
                                          minWidth: 68,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 6,
                                          fontWeight: 900,
                                          color:
                                            gate.state === 'complete'
                                              ? '#16a34a'
                                              : gate.state === 'in_progress'
                                                ? '#d97706'
                                                : '#111827',
                                        }}
                                        title={
                                          gate.state === 'complete'
                                            ? 'Complete'
                                            : gate.state === 'in_progress'
                                              ? 'In progress'
                                              : 'Not started (previous checkpoint not complete)'
                                        }
                                      >
                                        {gate.state === 'complete' ? '✅' : gate.state === 'in_progress' ? '⚠️' : null}
                                        <span>{pct(gate.percent)}</span>
                                      </div>
                                      <div style={{ fontWeight: 700 }}>{checkpoint.title}</div>
                                    </div>
                                    <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                                      {logisticCount} logistics
                                    </div>
                                  </div>
                                  {checkpoint.helpText ? <div className="muted" style={{ fontSize: 13 }}>{checkpoint.helpText}</div> : null}
                                </div>
                                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                  {(() => {
                                    const missing = missingLogisticsByCheckpointId[String(checkpoint.checkpointId)] || [];
                                    if (!missing || missing.length === 0) return null;
                                    return (
                                      <button
                                        type="button"
                                        className="mini-btn"
                                        title="Copies an email requesting missing items in this checkpoint"
                                        onClick={() => {
                                          const contact = applicablePartnerContacts.find((c: any) => (c as any).isPrimaryContact === true) || null;
                                          const sender =
                                            String((userData as any)?.displayName || '').trim()
                                            || String((userData as any)?.email || '').trim()
                                            || String((userData as any)?.id || '').trim()
                                            || '—';
                                          const email = buildMissingLogisticsEmail({
                                            schoolName: String((selectedLocation as any)?.name || (selectedPartner as any)?.name || '').trim(),
                                            programName: String((selectedSquad as any)?.title || '').trim() || 'our program',
                                            checkpointName: String(checkpoint.title || checkpoint.checkpointId || '').trim(),
                                            contactName: String((contact as any)?.displayName || '').trim(),
                                            senderName: sender,
                                            missing: missing.map(x => ({ title: x.title, required: Boolean((x as any).required), order: Number((x as any).order ?? 0) })),
                                          });
                                          void (async () => {
                                            const ok = await copyTextToClipboard(email);
                                            showCopyToast(ok ? 'Copied to clipboard' : 'Copy failed');
                                          })();
                                        }}
                                      >
                                        Copy email
                                      </button>
                                    );
                                  })()}
                                  {isEditMode ? (
                                    <button
                                      type="button"
                                      className="mini-btn"
                                      onClick={() => updateCheckpointOrderOverride(checkpoint.checkpointId, null)}
                                      title="Clear custom ordering"
                                    >
                                      Reset order
                                    </button>
                                  ) : null}
                                  {isEditMode ? (
                                    <button
                                      type="button"
                                      className="mini-btn danger"
                                      onClick={() => updateCheckpoint(checkpoint.checkpointId, { status: 'superseded' as OpsCheckpointStatus })}
                                      title="Remove checkpoint from this flow (hidden by default)"
                                    >
                                      Remove
                                    </button>
                                  ) : null}
                                </div>
                              </div>

                              {!isCheckpointCollapsed && (
                              <div style={{ marginTop: 8 }}>
                                {tasks.length === 0 ? (
                                  <div className="muted" style={{ fontSize: 13 }}>No tasks.</div>
                                ) : (
                                  tasks.map((t) => {
                                    const logisticId = String((t as any).logisticId || '').trim();
                                    const logistic = logisticId ? (logisticsById[logisticId] || null) : null;
                                    let value: any = undefined;
                                    let derivedOk: boolean | undefined = undefined;
                                    if (logistic) {
                                      if (logistic.scope === 'derived' && logistic.storage.kind === 'derived') {
                                        derivedOk = derivedOkByRuleId[logistic.storage.ruleId];
                                        value = derivedValueByRuleId[logistic.storage.ruleId];
                                      } else if (logistic.storage.kind === 'schools') {
                                        value = selectedFlow?.readOnlyHistorical
                                          ? getValueAtPath(legacySchool as any, logistic.storage.fieldPath)
                                          : undefined;
                                      } else if (logistic.storage.kind === 'billing_profile') {
                                        value = getValueAtPath(billingProfile as any, logistic.storage.fieldPath);
                                      } else if (logistic.storage.kind === 'partners') {
                                        value = getValueAtPath(selectedPartner as any, logistic.storage.fieldPath);
                                      } else if (logistic.storage.kind === 'locations') {
                                        value = getValueAtPath(selectedLocation as any, logistic.storage.fieldPath);
                                      } else if (logistic.storage.kind === 'partner_seasons') {
                                        value = getValueAtPath(partnerSeason as any, logistic.storage.fieldPath);
                                      } else if (logistic.storage.kind === 'squads') {
                                        value = getValueAtPath(selectedSquad as any, logistic.storage.fieldPath);
                                      }
                                    }
                                    // Check draft value for immediate status update
                                    const draftKey = logistic ? getLocalDraftKey(logistic) : '';
                                    const hasDraft = draftKey && Object.prototype.hasOwnProperty.call(logisticDraftById, draftKey);
                                    const effectiveValue = hasDraft ? logisticDraftById[draftKey] : value;
                                    const markedNotNeeded = logisticId ? isLogisticMarkedNotNeeded(selectedFlow as any, logisticId) : false;
                                    const isRemoved = Boolean(markedNotNeeded && logistic && !logistic.allowNotApplicable);
                                    const isNotApplicable = Boolean(markedNotNeeded && logistic && logistic.allowNotApplicable);
                                    const valueComplete = logistic ? isCompleteByRule(logistic, effectiveValue, derivedOk, false) : false;
                                    const complete = logistic ? isCompleteByRule(logistic, effectiveValue, derivedOk, isNotApplicable) : false;
                                    const st = (selectedFlow.taskState || {})[t.taskId] || { status: 'open' };
                                    const isSup = st.status === 'superseded';
                                    const isTextarea = logistic?.id === 'squad.commitmentConfirmation';
                                    const attemptMeta = (() => {
                                      if (!logisticId) return null;
                                      const key = logisticKeyFromId(logisticId);
                                      const m = ((selectedFlow as any)?.logisticAttemptMetaByKey || {}) as any;
                                      return (m && typeof m === 'object') ? (m[key] || null) : null;
                                    })();
                                    const outreachTooltip = attemptMeta
                                      ? `Last attempted: ${timeAgoLabel(Number(attemptMeta.lastAttemptAt || 0))} (${String(attemptMeta.lastAttemptChannel || '').replace(/_/g, ' ')})\nNext follow-up: ${daysFromNowLabel(Number(attemptMeta.nextFollowUpAt || 0))}`
                                      : '';
                                    return (
                                      <div
                                        key={t.taskId}
                                        style={{ padding: '8px 0', paddingLeft: 16, borderBottom: '1px solid #f3f4f6' }}
                                        title={outreachTooltip || undefined}
                                      >
                                        <div
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 12,
                                            flexWrap: 'wrap',
                                            position: 'relative',
                                            // Reserve space for the right-pinned Remove/Removed button.
                                            paddingRight: 130,
                                          }}
                                        >
                                          <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                            {logistic ? (
                                              <span
                                                title={isRemoved ? 'Removed' : (isNotApplicable && !valueComplete ? 'Not needed' : (complete ? 'Complete' : 'Missing'))}
                                                aria-label={isRemoved ? 'Removed' : (isNotApplicable && !valueComplete ? 'Not needed' : (complete ? 'Complete' : 'Missing'))}
                                                style={{
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  width: 16,
                                                  height: 16,
                                                  borderRadius: 999,
                                                  background: isRemoved ? '#9ca3af' : (complete ? '#22c55e' : '#ef4444'),
                                                  flexShrink: 0,
                                                }}
                                              >
                                                <span aria-hidden="true" />
                                              </span>
                                            ) : null}
                                          </div>
                                          <span
                                            style={{
                                              fontSize: 14,
                                              fontWeight: 600,
                                              color: isRemoved || isSup ? '#9ca3af' : '#111827',
                                              textDecoration: isRemoved ? 'line-through' : 'none',
                                              whiteSpace: 'nowrap',
                                            }}
                                          >
                                            {t.title}
                                          </span>
                                          {logistic && !isSup ? (
                                            <button
                                              type="button"
                                              className="mini-btn"
                                              style={{
                                                position: 'absolute',
                                                right: 0,
                                                top: 0,
                                                whiteSpace: 'nowrap',
                                                ...(logistic.allowNotApplicable
                                                  ? (isRemoved
                                                    ? { background: '#f3f4f6', border: '1px solid #d1d5db', color: '#6b7280' }
                                                    : null)
                                                  : ({
                                                    width: 28,
                                                    height: 28,
                                                    padding: 0,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: 18,
                                                    lineHeight: '28px',
                                                    borderRadius: 999,
                                                    background: isRemoved ? '#f3f4f6' : '#fee2e2',
                                                    border: `1px solid ${isRemoved ? '#d1d5db' : '#fecaca'}`,
                                                    color: isRemoved ? '#6b7280' : '#b91c1c',
                                                  } as React.CSSProperties)),
                                              }}
                                              onClick={() => void toggleLogisticNotNeeded(logisticId, !markedNotNeeded)}
                                              title={
                                                logistic.allowNotApplicable
                                                  ? (markedNotNeeded ? 'Click to undo “not needed”' : 'Marks this logistic as complete for this flow without filling it out')
                                                  : (markedNotNeeded ? 'Click to undo removal' : 'Removes this logistic from this flow (can be undone)')
                                              }
                                              aria-label={
                                                logistic.allowNotApplicable
                                                  ? (markedNotNeeded ? 'Undo not needed' : 'Mark not needed')
                                                  : (markedNotNeeded ? 'Undo remove' : 'Remove')
                                              }
                                            >
                                              {logistic.allowNotApplicable
                                                ? (markedNotNeeded ? 'Undo not needed' : 'Not needed')
                                                : '−'}
                                            </button>
                                          ) : null}
                                          {logisticId && !logistic ? <span style={{ fontSize: 11, color: '#9ca3af' }}>(unknown)</span> : null}
                                          {!isRemoved && logistic && !isCheckpointCollapsed && logistic.scope === 'derived' && logistic.storage.kind === 'derived' ? (
                                            <>
                                              {derivedEditError ? <span className="billing-error" style={{ fontSize: 11 }}>{derivedEditError}</span> : null}
                                              {logistic.type === 'boolean' ? (
                                                (() => {
                                                  const ruleId = String(logistic.storage.ruleId || '');
                                                  if (ruleId === 'enrollmentMeetsMin') {
                                                    const cur = derivedValueByRuleId[ruleId];
                                                    const v: boolean | null = typeof cur === 'boolean' ? cur : null;
                                                    const pick = (nextRaw: '' | 'false' | 'true') => {
                                                      void setEnrollmentMeetsMinOverride(
                                                        nextRaw === '' ? null : nextRaw === 'true'
                                                      );
                                                    };
                                                    return (
                                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                        {([
                                                          { id: '', label: '—', tone: 'neutral' as const },
                                                          { id: 'false', label: 'No', tone: 'no' as const },
                                                          { id: 'true', label: 'Yes', tone: 'yes' as const },
                                                        ] as const).map((opt) => {
                                                          const active =
                                                            opt.id === ''
                                                              ? v === null
                                                              : opt.id === 'true'
                                                                ? v === true
                                                                : v === false;
                                                          const colors =
                                                            opt.tone === 'yes'
                                                              ? { bg: '#ecfdf5', border: '#34d399', text: '#065f46' }
                                                              : opt.tone === 'no'
                                                                ? { bg: '#fee2e2', border: '#fca5a5', text: '#991b1b' }
                                                                : { bg: '#fff', border: '#e5e7eb', text: '#6b7280' };
                                                          return (
                                                            <button
                                                              key={opt.id || 'unset'}
                                                              type="button"
                                                              onClick={() => pick(opt.id)}
                                                              disabled={!isAdmin}
                                                              style={{
                                                                padding: '6px 10px',
                                                                borderRadius: 999,
                                                                border: `1px solid ${active ? colors.border : '#e5e7eb'}`,
                                                                background: active ? colors.bg : 'white',
                                                                color: active ? colors.text : '#6b7280',
                                                                fontWeight: 800,
                                                                fontSize: 12,
                                                                cursor: isAdmin ? 'pointer' : 'not-allowed',
                                                                opacity: isAdmin ? 1 : 0.7,
                                                                whiteSpace: 'nowrap',
                                                              }}
                                                              title={isAdmin ? 'Set value' : 'Admins only'}
                                                            >
                                                              {opt.label}
                                                            </button>
                                                          );
                                                        })}
                                                      </div>
                                                    );
                                                  }
                                                  const computed =
                                                    typeof value === 'boolean'
                                                      ? value
                                                      : (typeof derivedOk === 'boolean' ? derivedOk : null);
                                                  if (computed === null) return <span className="muted" style={{ fontSize: 12 }}>—</span>;
                                                  return (
                                                    <span
                                                      style={{
                                                        fontSize: 12,
                                                        fontWeight: 900,
                                                        color: computed ? '#16a34a' : '#b91c1c',
                                                        background: computed ? '#dcfce7' : '#fee2e2',
                                                        border: `1px solid ${computed ? '#86efac' : '#fecaca'}`,
                                                        padding: '2px 8px',
                                                        borderRadius: 999,
                                                      }}
                                                    >
                                                      {computed ? 'Yes' : 'No'}
                                                    </span>
                                                  );
                                                })()
                                              ) : null}
                                              {logistic.type === 'person' ? (
                                                (() => {
                                                  const ruleId = String(logistic.storage.ruleId || '');
                                                  const role = roleForPersonRuleId(ruleId);
                                                  // For tag roles, find by flag; for job roles, find by role
                                                  const existing = isTagRole(ruleId)
                                                    ? findContactWithTagRole(ruleId)
                                                    : (role ? applicablePartnerContacts.find(c => c.role === role) : null);
                                                  const pickId = String(personPickByRuleId[ruleId] || '');
                                                  const teacherChampionOptional = ruleId === 'schoolTeacherChampionExists'
                                                    ? Boolean((selectedPartner as any)?.teacherChampionOptional)
                                                    : false;
                                                  return (
                                                    <>
                                                      {existing ? (
                                                        <span style={{ fontSize: 13 }}>
                                                          <span style={{ fontWeight: 600 }}>{existing.displayName}</span>
                                                          <span className="muted" style={{ marginLeft: 6 }}>
                                                            {existing.email ? existing.email : ''}
                                                            {existing.phone ? (existing.email ? ' • ' : '') + existing.phone : ''}
                                                          </span>
                                                        </span>
                                                      ) : null}
                                                      {!existing && ruleId === 'schoolTeacherChampionExists' ? (
                                                        <span className="muted" style={{ fontSize: 12 }}>
                                                          {teacherChampionOptional ? 'Marked as: no teacher champion' : 'No teacher champion set'}
                                                        </span>
                                                      ) : null}
                                                      <SearchableSelect
                                                        valueId={pickId}
                                                        placeholder="Search contacts…"
                                                        options={contactOptions}
                                                        disabled={false}
                                                        onSelectId={(id) => {
                                                          setPersonPickByRuleId(prev => ({ ...prev, [ruleId]: id }));
                                                          void assignExistingContactToRole(ruleId, id);
                                                        }}
                                                      />
                                                      {ruleId === 'schoolTeacherChampionExists' ? (
                                                        <button
                                                          type="button"
                                                          className="mini-btn"
                                                          onClick={() => void setTeacherChampionOptional(!teacherChampionOptional)}
                                                          title="Mark this logistic as not required for this partner"
                                                          style={{ opacity: existing ? 0.6 : 1 }}
                                                          disabled={Boolean(existing)}
                                                        >
                                                          {teacherChampionOptional ? 'Undo “no champion”' : 'No teacher champion'}
                                                        </button>
                                                      ) : null}
                                                      <button
                                                        type="button"
                                                        className="mini-btn"
                                                        onClick={() => {
                                                          ensurePersonDraft(ruleId);
                                                          setCreateContactModalRuleId(ruleId);
                                                        }}
                                                      >
                                                        Create contact
                                                      </button>
                                                    </>
                                                  );
                                                })()
                                              ) : null}

                                            {logistic.storage.ruleId === 'billingProfileExists' ? (
                                              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                                <button type="button" className="mini-btn" onClick={() => void openBillingProfileEditor()}>
                                                  {derivedOk ? 'Edit billing profile' : 'Create billing profile'}
                                                </button>
                                                <button type="button" className="mini-btn" onClick={() => navigate(`/app/admin/partners/${effectivePartnerId}?tab=billing`)}>
                                                  Open partner billing
                                                </button>
                                              </div>
                                            ) : null}

                                            {logistic.storage.ruleId === 'approvedBillingRecipientExists' ? (
                                              <div style={{ marginTop: 8 }}>
                                                <div style={{ fontWeight: 800, marginBottom: 8 }}>Authorized billing contacts</div>
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 8, alignItems: 'center' }}>
                                                  <SearchableSelect
                                                    valueId={billingAuthorizedPickId}
                                                    placeholder="Select contact…"
                                                    options={contactOptions}
                                                    disabled={false}
                                                    onSelectId={(id) => setBillingAuthorizedPickId(id)}
                                                  />
                                                  <button
                                                    type="button"
                                                    className="mini-btn"
                                                    onClick={() => {
                                                      const id = String(billingAuthorizedPickId || '').trim();
                                                      if (!id) return;
                                                      if (billingAuthorizedContactIds.includes(id)) return;
                                                      const next = [...billingAuthorizedContactIds, id];
                                                      setBillingAuthorizedContactIds(next);
                                                      setDerivedOkByRuleId(prev => ({ ...prev, approvedBillingRecipientExists: true }));
                                                      void setDoc(
                                                        doc(db, 'partners', effectivePartnerId),
                                                        {
                                                          billingDefaults: { ...(billingProfile || {}), authorizedContactIds: next },
                                                          updatedAt: new Date(),
                                                          updatedBy: String((userData as any)?.id || ''),
                                                        } as any,
                                                        { merge: true }
                                                      );
                                                      setBillingAuthorizedPickId('');
                                                    }}
                                                  >
                                                    Add
                                                  </button>
                                                </div>

                                                <div style={{ marginTop: 10 }}>
                                                  {billingAuthorizedContactIds.length === 0 ? (
                                                    <div className="muted" style={{ fontSize: 12 }}>No authorized contacts yet.</div>
                                                  ) : (
                                                    <div style={{ display: 'grid', gap: 8 }}>
                                                      {billingAuthorizedContactIds.map((cid) => {
                                                        const c = applicablePartnerContacts.find(x => String(x.id) === String(cid)) as any;
                                                        const name = String(c?.displayName || cid);
                                                        const email = String(c?.email || '').trim();
                                                        return (
                                                          <div key={cid} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 10, background: 'white' }}>
                                                            <div style={{ minWidth: 0 }}>
                                                              <div style={{ fontWeight: 800, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                                                              <div className="muted" style={{ fontSize: 12 }}>{email || 'No email on contact'}</div>
                                                            </div>
                                                            <button
                                                              type="button"
                                                              className="mini-btn danger"
                                                              onClick={() => {
                                                                const next = billingAuthorizedContactIds.filter(x => String(x) !== String(cid));
                                                                setBillingAuthorizedContactIds(next);
                                                                setDerivedOkByRuleId(o => ({ ...o, approvedBillingRecipientExists: next.length > 0 }));
                                                                void setDoc(
                                                                  doc(db, 'partners', effectivePartnerId),
                                                                  {
                                                                    billingDefaults: { ...(billingProfile || {}), authorizedContactIds: next },
                                                                    updatedAt: new Date(),
                                                                    updatedBy: String((userData as any)?.id || ''),
                                                                  } as any,
                                                                  { merge: true }
                                                                );
                                                              }}
                                                            >
                                                              Remove
                                                            </button>
                                                          </div>
                                                        );
                                                      })}
                                                    </div>
                                                  )}
                                                </div>

                                                <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                                                  Stored in `partners/{effectivePartnerId}.billingDefaults.authorizedContactIds`.
                                                </div>
                                              </div>
                                            ) : null}

                                            {logistic.storage.ruleId === 'squadHalfwayStatus' ? (
                                              (() => {
                                                const st = String(value || '').trim();
                                                const tone =
                                                  st === 'green' ? { bg: '#22c55e', label: 'Green' } :
                                                  st === 'yellow' ? { bg: '#eab308', label: 'Yellow' } :
                                                  st === 'red' ? { bg: '#ef4444', label: 'Red' } :
                                                  null;
                                                if (!tone) return <span className="muted" style={{ fontSize: 12 }}>—</span>;
                                                return (
                                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                                    <span style={{ width: 10, height: 10, borderRadius: 999, background: tone.bg }} />
                                                    <span style={{ fontWeight: 800, fontSize: 13 }}>{tone.label}</span>
                                                  </span>
                                                );
                                              })()
                                            ) : null}

                                            {logistic.storage.ruleId === 'squadHalfwayNeedsIntervention' ? (
                                              <span style={{ fontWeight: 800, fontSize: 13 }}>
                                                {value === true ? 'Yes' : value === false ? 'No' : '—'}
                                              </span>
                                            ) : null}

                                            {/* Generic fallback for read-only derived values */}
                                            {logistic.storage.ruleId !== 'billingProfileExists'
                                              && logistic.storage.ruleId !== 'approvedBillingRecipientExists'
                                              && logistic.storage.ruleId !== 'squadHalfwayStatus'
                                              && logistic.storage.ruleId !== 'squadHalfwayNeedsIntervention'
                                              && logistic.type !== 'person' ? (
                                                <span className="muted" style={{ fontSize: 12 }}>
                                                  {logistic.type === 'boolean'
                                                    ? (derivedOk ? 'Yes' : 'No')
                                                    : (value === undefined || value === null || String(value).trim() === '' ? '—' : String(value))}
                                                </span>
                                              ) : null}
                                            </>
                                          ) : null}
                                          {logistic && !isCheckpointCollapsed && !(logistic.scope === 'derived' && logistic.storage.kind === 'derived') ? (
                                            (() => {
                                                  const key = getLocalDraftKey(logistic);
                                                  const hasDraft = Object.prototype.hasOwnProperty.call(logisticDraftById, key);
                                                  const draft = logisticDraftById[key];
                                                  const display = hasDraft
                                                    ? (typeof draft === 'string' ? draft : getDisplayStringForLogistic(logistic, draft))
                                                    : getDisplayStringForLogistic(logistic, value);
                                                  const dirty = Boolean(logisticDirtyById[key]);
                                                  const err = logisticSaveErrorById[key];

                                                  if (logistic.id === 'squad.firstDayTeacherPacket') {
                                                    return (
                                                      <FirstDayTeacherPacketLogistic
                                                        squadId={String(selectedFlow?.squadId || selectedFlow?.squadIds?.[0] || '')}
                                                        partnerId={effectivePartnerId}
                                                        locationId={effectiveLocationId}
                                                        legacySchoolId={selectedFlow?.readOnlyHistorical ? String(selectedFlow.schoolId || '') : undefined}
                                                        packetValue={value}
                                                        squad={selectedSquad as any}
                                                        location={selectedLocation as any}
                                                        legacySchool={selectedFlow?.readOnlyHistorical ? legacySchool : null}
                                                        partnerContacts={applicablePartnerContacts as any}
                                                        currentUserId={String((userData as any)?.id || '')}
                                                      />
                                                    );
                                                  }

                                                  if (logistic.id === 'squad.leadTeacherId') {
                                                    return (
                                                      <LeadTeacherAssignmentLogistic
                                                        squadId={String(selectedFlow?.squadId || '')}
                                                        currentValue={String(value || '')}
                                                        currentUserId={String((userData as any)?.id || '')}
                                                      />
                                                    );
                                                  }

                                                  if (logistic.id === 'squad.studentRoster') {
                                                    return (
                                                      <StudentRosterLogistic
                                                        squad={selectedSquad as any}
                                                        onSquadUpdate={(updated) => {
                                                          setSquads((prev) => prev.map((s) => (String((s as any)?.id) === String((updated as any)?.id) ? updated : s)));
                                                        }}
                                                      />
                                                    );
                                                  }

                                                  if (logistic.id === 'school.address' || logistic.id === 'location.address') {
                                                    const parseAddress = (addr: string) => {
                                                      const parts = String(addr || '').split(',').map(s => s.trim());
                                                      const street = parts[0] || '';
                                                      const city = parts[1] || '';
                                                      const stateZip = parts[2] || '';
                                                      const stateMatch = stateZip.match(/^([A-Za-z]{2})\s*(\d{5})?/);
                                                      const state = stateMatch?.[1] || stateZip.split(/\s+/)[0] || '';
                                                      const zip = stateMatch?.[2] || stateZip.split(/\s+/)[1] || '';
                                                      return { street, city, state, zip };
                                                    };
                                                    const parsed = parseAddress(display);
                                                    const addrKey = `${key}:addr`;
                                                    const hasAddrDraft = Object.prototype.hasOwnProperty.call(logisticDraftById, addrKey);
                                                    const addrDraft = hasAddrDraft
                                                      ? JSON.parse(logisticDraftById[addrKey] || '{}')
                                                      : parsed;
                                                    const updateAddrField = (field: string, val: string) => {
                                                      const next = { ...addrDraft, [field]: val };
                                                      setLogisticDraftById(prev => ({ ...prev, [addrKey]: JSON.stringify(next) }));
                                                      setLogisticDirtyById(prev => ({ ...prev, [key]: true }));
                                                    };
                                                    const compileAddress = () => {
                                                      const parts = [];
                                                      if (addrDraft.street) parts.push(addrDraft.street);
                                                      if (addrDraft.city) parts.push(addrDraft.city);
                                                      if (addrDraft.state || addrDraft.zip) parts.push(`${addrDraft.state || ''} ${addrDraft.zip || ''}`.trim());
                                                      return parts.join(', ');
                                                    };
                                                    const saveAddress = () => {
                                                      const compiled = compileAddress();
                                                      setLogisticDraftById(prev => ({ ...prev, [key]: compiled }));
                                                      void commitLogisticDraft(logistic, compiled);
                                                    };
                                                    return (
                                                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                                        <input
                                                          placeholder="Street"
                                                          value={addrDraft.street || ''}
                                                          onChange={(e) => updateAddrField('street', e.target.value)}
                                                          onBlur={saveAddress}
                                                          style={{ width: 140 }}
                                                        />
                                                        <input
                                                          placeholder="City"
                                                          value={addrDraft.city || ''}
                                                          onChange={(e) => updateAddrField('city', e.target.value)}
                                                          onBlur={saveAddress}
                                                          style={{ width: 100 }}
                                                        />
                                                        <input
                                                          placeholder="State"
                                                          value={addrDraft.state || ''}
                                                          onChange={(e) => updateAddrField('state', e.target.value)}
                                                          onBlur={saveAddress}
                                                          style={{ width: 50 }}
                                                        />
                                                        <input
                                                          placeholder="ZIP"
                                                          value={addrDraft.zip || ''}
                                                          onChange={(e) => updateAddrField('zip', e.target.value)}
                                                          onBlur={saveAddress}
                                                          style={{ width: 70 }}
                                                        />
                                                        {err ? (
                                                          <span className="billing-error" style={{ fontSize: 11 }}>{err}</span>
                                                        ) : null}
                                                      </div>
                                                    );
                                                  }

                                                  if (logistic.id === 'location.pocContactId'
                                                    || (selectedFlow?.readOnlyHistorical && logistic.id === 'school.pocContactId')) {
                                                    const curId = String((hasDraft ? logisticDraftById[key] : value) ?? '').trim();
                                                    const selected = curId ? applicablePartnerContacts.find(c => String(c.id) === curId) : null;
                                                    return (
                                                      <div>
                                                        <select
                                                          value={curId}
                                                          onChange={(e) => {
                                                            const next = e.target.value;
                                                            if (next === '__add_new__') {
                                                              setMainPocCreateError(null);
                                                              setMainPocDraft({ firstName: '', lastName: '', email: '', phone: '' });
                                                              setMainPocCreateOpen(true);
                                                              return;
                                                            }
                                                            setLogisticDraftById(prev => ({ ...prev, [key]: next }));
                                                            setLogisticDirtyById(prev => ({ ...prev, [key]: true }));
                                                            void commitLogisticDraft(logistic, next);
                                                          }}
                                                        >
                                                          <option value="">— Select contact —</option>
                                                          {applicablePartnerContacts.map((c) => (
                                                            <option key={c.id} value={c.id}>
                                                              {String(c.displayName || '').trim() || '(no name)'}
                                                            </option>
                                                          ))}
                                                          <option value="__add_new__">+ Add contact…</option>
                                                        </select>
                                                        {selected ? (
                                                          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                                                            {String((selected as any)?.email || '').trim() ? `Email: ${(selected as any).email}` : ''}
                                                            {String((selected as any)?.email || '').trim() && String((selected as any)?.phone || '').trim() ? ' • ' : ''}
                                                            {String((selected as any)?.phone || '').trim() ? `Phone: ${(selected as any).phone}` : ''}
                                                          </div>
                                                        ) : null}
                                                        {err ? (
                                                          <div className="billing-error" style={{ marginTop: 6 }}>
                                                            {err}
                                                          </div>
                                                        ) : null}
                                                      </div>
                                                    );
                                                  }

                                                  if (logistic.type === 'contact_date') {
                                                    const cur = hasDraft ? logisticDraftById[key] : value;
                                                    const obj = (cur && typeof cur === 'object') ? cur : {};
                                                    const contactId = String((obj as any)?.contactId || '').trim();
                                                    const date = String((obj as any)?.date || '').trim().slice(0, 10);
                                                    const contact = contactId ? (applicablePartnerContacts.find(c => String(c.id) === contactId) || null) : null;

                                                    const setNext = (patch: Partial<{ contactId: string; date: string }>) => {
                                                      const next = {
                                                        contactId,
                                                        date,
                                                        ...patch,
                                                      };
                                                      setLogisticDraftById(prev => ({ ...prev, [key]: next }));
                                                      setLogisticDirtyById(prev => ({ ...prev, [key]: true }));
                                                      void commitLogisticDraft(logistic, next);
                                                    };

                                                    return (
                                                      <div>
                                                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                                                          <div style={{ width: 340, maxWidth: '60vw' }}>
                                                            <SearchableSelect
                                                              valueId={contactId}
                                                              placeholder="Select contact…"
                                                              options={contactOptions}
                                                              disabled={false}
                                                              onSelectId={(id) => setNext({ contactId: String(id || '').trim() })}
                                                            />
                                                          </div>
                                                          <button
                                                            type="button"
                                                            className="mini-btn"
                                                            onClick={() => {
                                                              setContactDateCreateError(null);
                                                              setContactDateDraft({ firstName: '', lastName: '', email: '', phone: '' });
                                                              setContactDateCreateCtx({ logisticId: logistic.id, key });
                                                            }}
                                                            title="Add a new contact"
                                                          >
                                                            + Contact
                                                          </button>
                                                          <input
                                                            type="date"
                                                            value={date}
                                                            onChange={(e) => setNext({ date: String(e.target.value || '').slice(0, 10) })}
                                                          />
                                                          {!contactId || !date ? (
                                                            <span className="muted" style={{ fontSize: 12 }}>
                                                              {(!contactId && !date) ? 'Pick contact + date' : (!contactId ? 'Pick contact' : 'Pick date')}
                                                            </span>
                                                          ) : null}
                                                        </div>
                                                        {contact ? (
                                                          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                                                            {String((contact as any)?.email || '').trim() ? `Email: ${(contact as any).email}` : ''}
                                                            {String((contact as any)?.email || '').trim() && String((contact as any)?.phone || '').trim() ? ' • ' : ''}
                                                            {String((contact as any)?.phone || '').trim() ? `Phone: ${(contact as any).phone}` : ''}
                                                          </div>
                                                        ) : null}
                                                        {err ? (
                                                          <div className="billing-error" style={{ marginTop: 6 }}>
                                                            {err}
                                                          </div>
                                                        ) : null}
                                                      </div>
                                                    );
                                                  }

                                                  if (logistic.type === 'enum' && Array.isArray(logistic.enumOptions)) {
                                                    const hasLegacy =
                                                      Boolean(display)
                                                      && !logistic.enumOptions.some(o => String(o.id) === String(display));
                                                    return (
                                                      <div>
                                                        <select
                                                          value={display}
                                                          onChange={(e) => {
                                                            const next = e.target.value;
                                                            setLogisticDraftById(prev => ({ ...prev, [key]: next }));
                                                            setLogisticDirtyById(prev => ({ ...prev, [key]: true }));
                                                            void commitLogisticDraft(logistic, next);
                                                          }}
                                                        >
                                                          <option value="">—</option>
                                                          {hasLegacy ? (
                                                            <option value={display}>{String(display)} (current)</option>
                                                          ) : null}
                                                          {logistic.enumOptions.map(opt => (
                                                            <option key={opt.id} value={opt.id}>{opt.label}</option>
                                                          ))}
                                                        </select>
                                                        {err ? (
                                                          <div className="billing-error" style={{ marginTop: 6 }}>
                                                            {err}
                                                          </div>
                                                        ) : null}
                                                      </div>
                                                    );
                                                  }

                                                  if (logistic.type === 'boolean') {
                                                    const rawLocal = hasDraft
                                                      ? String(logisticDraftById[key] ?? '')
                                                      : (value === true ? 'true' : value === false ? 'false' : '');
                                                    const v = rawLocal.trim().toLowerCase();
                                                    const pick = (nextRaw: '' | 'false' | 'true') => {
                                                      setLogisticDraftById(prev => ({ ...prev, [key]: nextRaw }));
                                                      setLogisticDirtyById(prev => ({ ...prev, [key]: true }));
                                                      void commitLogisticDraft(logistic, nextRaw);
                                                    };
                                                    return (
                                                      <div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                          {([
                                                            { id: '', label: '—', tone: 'neutral' as const },
                                                            { id: 'false', label: 'No', tone: 'no' as const },
                                                            { id: 'true', label: 'Yes', tone: 'yes' as const },
                                                          ] as const).map((opt) => {
                                                            const active =
                                                              opt.id === ''
                                                                ? v === ''
                                                                : opt.id === 'true'
                                                                  ? v === 'true'
                                                                  : v === 'false';
                                                            const colors =
                                                              opt.tone === 'yes'
                                                                ? { bg: '#ecfdf5', border: '#34d399', text: '#065f46' }
                                                                : opt.tone === 'no'
                                                                  ? { bg: '#f3f4f6', border: '#d1d5db', text: '#374151' }
                                                                  : { bg: '#fff', border: '#e5e7eb', text: '#6b7280' };
                                                            return (
                                                              <button
                                                                key={opt.id || 'unset'}
                                                                type="button"
                                                                onClick={() => pick(opt.id)}
                                                                style={{
                                                                  padding: '6px 10px',
                                                                  borderRadius: 999,
                                                                  border: `1px solid ${active ? colors.border : '#e5e7eb'}`,
                                                                  background: active ? colors.bg : 'white',
                                                                  color: active ? colors.text : '#6b7280',
                                                                  fontWeight: 800,
                                                                  fontSize: 12,
                                                                  cursor: 'pointer',
                                                                  whiteSpace: 'nowrap',
                                                                }}
                                                                title="Set value"
                                                              >
                                                                {opt.label}
                                                              </button>
                                                            );
                                                          })}
                                                        </div>
                                                        {err ? (
                                                          <div className="billing-error" style={{ marginTop: 6 }}>
                                                            {err}
                                                          </div>
                                                        ) : null}
                                                      </div>
                                                    );
                                                  }

                                                  if (logistic.type === 'date_list') {
                                                    const items = Array.isArray(value) ? (value as any[]).map(v => String(v || '').trim()).filter(Boolean) : [];
                                                    const pending = String(logisticDraftById[key] ?? '');
                                                    const addDate = async (ymd: string) => {
                                                      const cleaned = String(ymd || '').trim();
                                                      if (!cleaned) return;
                                                      const next = Array.from(new Set([...items, cleaned])).sort();
                                                      try {
                                                        await saveLogisticValue(logistic, next);
                                                        setLogisticDraftById(prev => ({ ...prev, [key]: '' }));
                                                        setLogisticSaveErrorById(prev => {
                                                          const { [key]: _ignore, ...rest } = prev;
                                                          return rest;
                                                        });
                                                      } catch (ex: any) {
                                                        setLogisticSaveErrorById(prev => ({ ...prev, [key]: String(ex?.message || 'Failed to save.') }));
                                                      }
                                                    };
                                                    const removeDate = async (ymd: string) => {
                                                      const cleaned = String(ymd || '').trim();
                                                      const next = items.filter(x => String(x) !== cleaned);
                                                      try {
                                                        await saveLogisticValue(logistic, next.length ? next : null);
                                                        setLogisticSaveErrorById(prev => {
                                                          const { [key]: _ignore, ...rest } = prev;
                                                          return rest;
                                                        });
                                                      } catch (ex: any) {
                                                        setLogisticSaveErrorById(prev => ({ ...prev, [key]: String(ex?.message || 'Failed to save.') }));
                                                      }
                                                    };
                                                    return (
                                                      <div>
                                                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                                                          <input
                                                            type="date"
                                                            value={pending}
                                                            onChange={(e) => setLogisticDraftById(prev => ({ ...prev, [key]: e.target.value }))}
                                                          />
                                                          <button
                                                            type="button"
                                                            className="mini-btn"
                                                            onClick={() => void addDate(pending)}
                                                            disabled={!pending}
                                                          >
                                                            Add date
                                                          </button>
                                                          <div className="muted" style={{ fontSize: 12 }}>
                                                            {items.length ? `${items.length} selected` : 'No dates yet'}
                                                          </div>
                                                        </div>
                                                        {items.length ? (
                                                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                                                            {items.map(d => (
                                                              <button
                                                                key={d}
                                                                type="button"
                                                                className="mini-btn"
                                                                onClick={() => void removeDate(d)}
                                                                title="Remove date"
                                                              >
                                                                {d} ×
                                                              </button>
                                                            ))}
                                                          </div>
                                                        ) : null}
                                                        {err ? (
                                                          <div className="billing-error" style={{ marginTop: 6 }}>
                                                            {err}
                                                          </div>
                                                        ) : null}
                                                      </div>
                                                    );
                                                  }

                                                  if (logistic.type === 'string_list' || logistic.type === 'file_list') {
                                                    return (
                                                      <div style={{ flex: '1 1 760px', minWidth: 320 }}>
                                                        <textarea
                                                          rows={4}
                                                          value={display}
                                                          onChange={(e) => {
                                                            const next = e.target.value;
                                                            setLogisticDraftById(prev => ({ ...prev, [key]: next }));
                                                            setLogisticDirtyById(prev => ({ ...prev, [key]: true }));
                                                          }}
                                                          onBlur={() => {
                                                            if (!dirty) return;
                                                            void commitLogisticDraft(logistic);
                                                          }}
                                                          placeholder={logistic.type === 'file_list' ? 'One URL per line (or upload files below)…' : 'One item per line…'}
                                                          style={{ width: '100%', resize: 'vertical' }}
                                                        />
                                                        {logistic.type === 'file_list' ? (
                                                          <div style={{ marginTop: 8 }}>
                                                            <input
                                                              type="file"
                                                              multiple
                                                              accept={logistic.ui?.accept || '*/*'}
                                                              onChange={(e) => {
                                                                const files = Array.from(e.target.files || []);
                                                                if (files.length === 0) return;
                                                                void (async () => {
                                                                  for (const f of files) {
                                                                    await uploadFileForLogistic(logistic, f, { appendToList: true });
                                                                  }
                                                                })();
                                                                e.currentTarget.value = '';
                                                              }}
                                                            />
                                                          </div>
                                                        ) : null}
                                                        {err ? (
                                                          <div className="billing-error" style={{ marginTop: 6 }}>
                                                            {err}
                                                          </div>
                                                        ) : null}
                                                      </div>
                                                    );
                                                  }

                                                  if (logistic.type === 'file') {
                                                    const url = String(value || '').trim();
                                                    const isImage = /\.(png|jpg|jpeg|gif|webp)$/i.test(url) || (logistic.ui?.accept || '').includes('image');
                                                    return (
                                                      <div>
                                                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                                          <input
                                                            type="file"
                                                            accept={logistic.ui?.accept || '*/*'}
                                                            onChange={(e) => {
                                                              const file = e.target.files?.[0];
                                                              if (!file) return;
                                                              void uploadFileForLogistic(logistic, file);
                                                              e.currentTarget.value = '';
                                                            }}
                                                          />
                                                          {url ? (
                                                            <a href={url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
                                                              Open
                                                            </a>
                                                          ) : null}
                                                        </div>
                                                        {isImage && url ? (
                                                          <div style={{ marginTop: 10 }}>
                                                            <img src={url} alt="" style={{ maxWidth: 280, maxHeight: 180, borderRadius: 10, border: '1px solid #e5e7eb' }} />
                                                          </div>
                                                        ) : null}
                                                        {err ? (
                                                          <div className="billing-error" style={{ marginTop: 6 }}>
                                                            {err}
                                                          </div>
                                                        ) : null}
                                                      </div>
                                                    );
                                                  }

                                                  return (
                                                    <div style={{ flex: '1 1 760px', minWidth: 320 }}>
                                                      {logistic.id === 'squad.commitmentConfirmation'
                                                      || [
                                                        'school.arrivalProcedure',
                                                        'school.dismissalProcedure',
                                                        'school.lateArrivalProcedure',
                                                        'school.arrivalDismissalProcedure',
                                                        'school.bathroomRules',
                                                        'school.hallwayRules',
                                                        'school.parkingInstructions',
                                                      ].includes(logistic.id) ? (
                                                        <textarea
                                                          rows={logistic.id === 'squad.commitmentConfirmation' ? 4 : 3}
                                                          value={display}
                                                          onChange={(e) => {
                                                            const next = e.target.value;
                                                            setLogisticDraftById(prev => ({ ...prev, [key]: next }));
                                                            setLogisticDirtyById(prev => ({ ...prev, [key]: true }));
                                                          }}
                                                          onBlur={() => {
                                                            if (!dirty) return;
                                                            void commitLogisticDraft(logistic);
                                                          }}
                                                          placeholder={logistic.helpText || ''}
                                                          style={{
                                                            width: '100%',
                                                            resize: 'vertical',
                                                          }}
                                                        />
                                                      ) : (
                                                      <input
                                                        type={
                                                          logistic.id === 'squad.priceCents' ||
                                                          logistic.id === 'squad.tuitionPerStudentPerClassCents' ||
                                                          logistic.id === 'squad.flatFeePerClassCents'
                                                            ? 'number'
                                                            : logistic.type === 'date'
                                                              ? 'date'
                                                              : logistic.type === 'number'
                                                                ? 'number'
                                                                : 'text'
                                                        }
                                                        inputMode={logistic.type === 'time' ? 'text' : undefined}
                                                        step={
                                                          logistic.id === 'squad.priceCents' ||
                                                          logistic.id === 'squad.tuitionPerStudentPerClassCents' ||
                                                          logistic.id === 'squad.flatFeePerClassCents'
                                                            ? '0.01'
                                                            : undefined
                                                        }
                                                        value={display}
                                                        onChange={(e) => {
                                                          const next =
                                                            logistic.type === 'time'
                                                              ? formatTimeDraftForTyping(e.target.value)
                                                              : e.target.value;
                                                          setLogisticDraftById(prev => ({ ...prev, [key]: next }));
                                                          setLogisticDirtyById(prev => ({ ...prev, [key]: true }));
                                                        }}
                                                        onKeyDown={(e) => {
                                                          if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
                                                        }}
                                                        onBlur={() => {
                                                          if (!dirty) return;
                                                          void commitLogisticDraft(logistic);
                                                        }}
                                                        placeholder={
                                                          logistic.id === 'squad.priceCents' ||
                                                          logistic.id === 'squad.tuitionPerStudentPerClassCents' ||
                                                          logistic.id === 'squad.flatFeePerClassCents'
                                                            ? '0.00'
                                                            : logistic.type === 'time'
                                                              ? '3:15pm'
                                                            : (logistic.helpText || '')
                                                        }
                                                        style={{
                                                          width: logistic.type === 'time' ? 140 : '100%',
                                                          fontVariantNumeric: 'tabular-nums',
                                                        }}
                                                      />
                                                      )}
                                                      {err ? (
                                                        <div className="billing-error" style={{ marginTop: 6 }}>
                                                          {err}
                                                        </div>
                                                      ) : null}
                                                    </div>
                                                  );
                                                })()
                                            ) : null}
                                        </div>
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                </div>
              );
            })}

            {billingProfileDraftOpen ? (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, marginTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                  <div style={{ fontWeight: 800 }}>Billing profile</div>
                  <button type="button" className="mini-btn" onClick={() => setBillingProfileDraftOpen(false)}>Close</button>
                </div>
                {derivedEditError ? <div className="billing-error" style={{ marginTop: 8 }}>{derivedEditError}</div> : null}
                <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 12, color: '#6b7280' }}>Legal name</label>
                    <input value={billingProfileDraft.legalName || ''} onChange={(e) => setBillingProfileDraft((p: any) => ({ ...p, legalName: e.target.value }))} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#6b7280' }}>Payment preference</label>
                    <select value={billingProfileDraft.paymentPreference || 'ach'} onChange={(e) => setBillingProfileDraft((p: any) => ({ ...p, paymentPreference: e.target.value }))}>
                      <option value="ach">ACH</option>
                      <option value="check">Check</option>
                      <option value="card">Card</option>
                      <option value="portal">Portal</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#6b7280' }}>Net invoice days</label>
                    <input type="number" value={billingProfileDraft.netInvoiceDays ?? ''} onChange={(e) => setBillingProfileDraft((p: any) => ({ ...p, netInvoiceDays: e.target.value === '' ? undefined : Number(e.target.value) }))} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#6b7280' }}>Net payment days</label>
                    <input type="number" value={billingProfileDraft.netPaymentDays ?? ''} onChange={(e) => setBillingProfileDraft((p: any) => ({ ...p, netPaymentDays: e.target.value === '' ? undefined : Number(e.target.value) }))} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: 12, color: '#6b7280' }}>Billing address line 1</label>
                    <input value={billingProfileDraft.billingAddress?.line1 || ''} onChange={(e) => setBillingProfileDraft((p: any) => ({ ...p, billingAddress: { ...(p.billingAddress || {}), line1: e.target.value } }))} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: 12, color: '#6b7280' }}>Billing address line 2</label>
                    <input value={billingProfileDraft.billingAddress?.line2 || ''} onChange={(e) => setBillingProfileDraft((p: any) => ({ ...p, billingAddress: { ...(p.billingAddress || {}), line2: e.target.value } }))} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#6b7280' }}>City</label>
                    <input value={billingProfileDraft.billingAddress?.city || ''} onChange={(e) => setBillingProfileDraft((p: any) => ({ ...p, billingAddress: { ...(p.billingAddress || {}), city: e.target.value } }))} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#6b7280' }}>State</label>
                    <input value={billingProfileDraft.billingAddress?.state || ''} onChange={(e) => setBillingProfileDraft((p: any) => ({ ...p, billingAddress: { ...(p.billingAddress || {}), state: e.target.value } }))} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#6b7280' }}>Postal code</label>
                    <input value={billingProfileDraft.billingAddress?.postalCode || ''} onChange={(e) => setBillingProfileDraft((p: any) => ({ ...p, billingAddress: { ...(p.billingAddress || {}), postalCode: e.target.value } }))} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#6b7280' }}>Country</label>
                    <input value={billingProfileDraft.billingAddress?.country || ''} onChange={(e) => setBillingProfileDraft((p: any) => ({ ...p, billingAddress: { ...(p.billingAddress || {}), country: e.target.value } }))} />
                  </div>
                </div>
                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-primary" onClick={() => void saveBillingProfile()}>Save billing profile</button>
                </div>
              </div>
            ) : null}

            {createContactModalRuleId ? (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ background: 'white', borderRadius: 12, padding: 20, width: 400, maxWidth: '90vw' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div style={{ fontWeight: 800, fontSize: 16 }}>Create Contact</div>
                    <button type="button" className="mini-btn" onClick={() => setCreateContactModalRuleId(null)}>×</button>
                  </div>
                  {derivedEditError ? <div className="billing-error" style={{ marginBottom: 12 }}>{derivedEditError}</div> : null}
                  {(() => {
                    const ruleId = createContactModalRuleId;
                    const draft = personDraftByRuleId[ruleId] || { firstName: '', lastName: '', email: '', phone: '' };
                    const label = labelForPersonRuleId(ruleId);
                    return (
                      <div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                          <div>
                            <label style={{ fontSize: 12, color: '#6b7280' }}>First name</label>
                            <input
                              value={draft.firstName}
                              placeholder="First name"
                              onChange={(e) => setPersonDraftByRuleId(prev => ({ ...prev, [ruleId]: { ...(prev[ruleId] || { firstName: '', lastName: '', email: '', phone: '' }), firstName: e.target.value } }))}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: 12, color: '#6b7280' }}>Last name</label>
                            <input
                              value={draft.lastName}
                              placeholder="Last name"
                              onChange={(e) => setPersonDraftByRuleId(prev => ({ ...prev, [ruleId]: { ...(prev[ruleId] || { firstName: '', lastName: '', email: '', phone: '' }), lastName: e.target.value } }))}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: 12, color: '#6b7280' }}>Email</label>
                            <input
                              value={draft.email}
                              placeholder="Email"
                              onChange={(e) => setPersonDraftByRuleId(prev => ({ ...prev, [ruleId]: { ...(prev[ruleId] || { firstName: '', lastName: '', email: '', phone: '' }), email: e.target.value } }))}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: 12, color: '#6b7280' }}>Cell phone</label>
                            <input
                              value={draft.phone}
                              placeholder="Cell phone"
                              onChange={(e) => setPersonDraftByRuleId(prev => ({ ...prev, [ruleId]: { ...(prev[ruleId] || { firstName: '', lastName: '', email: '', phone: '' }), phone: e.target.value } }))}
                            />
                          </div>
                        </div>
                        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                          <button type="button" className="mini-btn" onClick={() => setCreateContactModalRuleId(null)}>Cancel</button>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={async () => {
                              await createContactForRole(ruleId);
                              setCreateContactModalRuleId(null);
                            }}
                          >
                            Create {label}
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ) : null}

            {contactDateCreateCtx ? (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 102, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ background: 'white', borderRadius: 12, padding: 20, width: 420, maxWidth: '92vw' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div style={{ fontWeight: 900, fontSize: 16 }}>Add contact</div>
                    <button type="button" className="mini-btn" onClick={() => setContactDateCreateCtx(null)}>×</button>
                  </div>
                  {contactDateCreateError ? <div className="billing-error" style={{ marginBottom: 12 }}>{contactDateCreateError}</div> : null}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div>
                      <label style={{ fontSize: 12, color: '#6b7280' }}>First name</label>
                      <input
                        value={contactDateDraft.firstName}
                        placeholder="First name"
                        onChange={(e) => setContactDateDraft(prev => ({ ...prev, firstName: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: '#6b7280' }}>Last name</label>
                      <input
                        value={contactDateDraft.lastName}
                        placeholder="Last name"
                        onChange={(e) => setContactDateDraft(prev => ({ ...prev, lastName: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: '#6b7280' }}>Email</label>
                      <input
                        value={contactDateDraft.email}
                        placeholder="Email"
                        onChange={(e) => setContactDateDraft(prev => ({ ...prev, email: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: '#6b7280' }}>Phone</label>
                      <input
                        value={contactDateDraft.phone}
                        placeholder="Phone"
                        onChange={(e) => setContactDateDraft(prev => ({ ...prev, phone: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
                    <button type="button" className="mini-btn" onClick={() => setContactDateCreateCtx(null)}>
                      Cancel
                    </button>
                    <button type="button" className="btn btn-primary" onClick={() => void createContactAndAssignToContactDate()}>
                      Create
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {mainPocCreateOpen ? (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 101, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ background: 'white', borderRadius: 12, padding: 20, width: 420, maxWidth: '92vw' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div style={{ fontWeight: 900, fontSize: 16 }}>Add on-site POC</div>
                    <button type="button" className="mini-btn" onClick={() => setMainPocCreateOpen(false)}>×</button>
                  </div>
                  {mainPocCreateError ? <div className="billing-error" style={{ marginBottom: 12 }}>{mainPocCreateError}</div> : null}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div>
                      <label style={{ fontSize: 12, color: '#6b7280' }}>First name</label>
                      <input
                        value={mainPocDraft.firstName}
                        placeholder="First name"
                        onChange={(e) => setMainPocDraft(prev => ({ ...prev, firstName: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: '#6b7280' }}>Last name</label>
                      <input
                        value={mainPocDraft.lastName}
                        placeholder="Last name"
                        onChange={(e) => setMainPocDraft(prev => ({ ...prev, lastName: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: '#6b7280' }}>Email</label>
                      <input
                        value={mainPocDraft.email}
                        placeholder="Email"
                        onChange={(e) => setMainPocDraft(prev => ({ ...prev, email: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: '#6b7280' }}>Phone</label>
                      <input
                        value={mainPocDraft.phone}
                        placeholder="Phone"
                        onChange={(e) => setMainPocDraft(prev => ({ ...prev, phone: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
                    <button type="button" className="mini-btn" onClick={() => setMainPocCreateOpen(false)}>
                      Cancel
                    </button>
                    <button type="button" className="btn btn-primary" onClick={() => void createAndAssignMainPoc()}>
                      Create & assign
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
